import dayjs from 'dayjs';
import { toastConfig } from 'config';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { withValueCap } from 'pages/globals/global';
import { NumericFormat } from 'react-number-format';
import InputLabel from 'components/InputLabel/index';
import constantFormValues from '../utils/formInputNames';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetQueryV3Mutation } from 'store/features/api/graphSlice';
import { useSendControlDataMutation } from 'store/features/api/apiSlice';
import { MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { useIdentityBulkMutation } from 'store/features/process/processApi';
import { utcConverter, processArrays, stateUpdater } from '../utils/helperFunc';
import ComptrollerConfirmModal from 'pages/comptroller/ComptrollerConfirmModal';
import { Col, DatePicker, Form, Input, Row, Select, message, Button, Spin, Tooltip } from 'antd';
import { Box, Stack, Table, useTheme, Typography, TableRow, TableHead, TableBody, TableCell, TableFooter, TableContainer } from '@mui/material';
import AddIconButton from 'components/button/AddIconButton';
import DeleteIconButton from 'components/button/DeleteIconButton';

const LeaseRecordManagementV9ModifyBasicInfo = ({
	screen,
	vendorList,
	handleSubmit,
	viewData = {},
	dataFullyLoaded,
	initialChangeLength = 0,
	debouncedManageChangeRows,
}) => {
	const form = Form.useFormInstance();
	const LeaseEndDate = dayjs().add(1, 'month');

	const currentLeaseTerm = Form.useWatch(constantFormValues?.leaseTerm?.key, form);
	const currentPaymentFrequency = Form.useWatch(constantFormValues?.paymentFrequency?.key, form);

	useEffect(() => {
		const currentPaymentFrequency = form.getFieldValue(constantFormValues?.paymentFrequency?.key);

		if (currentPaymentFrequency === 'Quarterly') {
			form.setFieldValue('modifiedLeaseTerm', Math.floor(form.getFieldValue('leaseTerm') / 3));
		} else {
			form.setFieldValue('modifiedLeaseTerm', form.getFieldValue('leaseTerm'));
		}
	}, [currentLeaseTerm, currentPaymentFrequency]);

	const grossRentKey = useRef('');
	const isUpdatingRef = useRef(false);
	const advanceAmountKey = useRef('');

	const grossRentList = Form.useWatch('grossRentList_new', form);
	const advanceAmountList = Form.useWatch('advanceAmountList_new', form);

	useEffect(() => {
		if (!Array.isArray(grossRentList) || !Array.isArray(advanceAmountList) || grossRentList.length === 0 || advanceAmountList.length === 0)
			return;

		const newGrossRentKey = grossRentList.map((item) => `${item.from}-${item.to}`).join('|');
		const newAdvanceAmountKey = advanceAmountList.map((item) => `${item.from}-${item.to}`).join('|');

		if (newGrossRentKey === grossRentKey.current && newAdvanceAmountKey === advanceAmountKey.current) return;

		if (isUpdatingRef.current) return;

		isUpdatingRef.current = true;

		try {
			grossRentKey.current = newGrossRentKey;
			advanceAmountKey.current = newAdvanceAmountKey;
			const maxGrossRentTerm = Math.max(...grossRentList.map((item) => item.to ?? 0));
			const maxAdvanceAmountTerm = Math.max(...advanceAmountList.map((item) => item.to ?? 0));
			const maxTerm = Math.max(maxGrossRentTerm, maxAdvanceAmountTerm);
			const currentLeaseTerm = form.getFieldValue('leaseTerm_new');

			if (currentLeaseTerm !== maxTerm) {
				form.setFieldValue('leaseTerm_new', maxTerm);
			}

			const leaseStartDate = form.getFieldValue('leaseStartDate');

			if (leaseStartDate && maxTerm) {
				const leaseEndDate = leaseStartDate.add(maxTerm, 'month').subtract(1, 'day');
				const currentLeaseEndDate = form.getFieldValue('leaseEndDate_new');

				if (!currentLeaseEndDate || !leaseEndDate.isSame(currentLeaseEndDate)) {
					form.setFieldValue('leaseEndDate_new', leaseEndDate);
				}
			}
		} finally {
			setTimeout(() => {
				isUpdatingRef.current = false;
			}, 0);
		}
	}, [grossRentList, advanceAmountList, screen]);

	const handlePaymentFrequencyChange = (value) => {
		if (isUpdatingRef.current) return value;

		isUpdatingRef.current = true;

		try {
			// callDebounced();
		} finally {
			isUpdatingRef.current = false;
		}

		return value;
	};

	const handlePaymentFrequencyBlur = () => {
		debouncedManageChangeRows();
	};

	const handlePaymentModalityChange = (value) => {
		if (isUpdatingRef.current) return value;

		isUpdatingRef.current = true;

		return value;
	};

	const handlePaymentModalityBlur = () => {
		debouncedManageChangeRows();
	};

	const callDebounced = () => {
		debouncedManageChangeRows();
	};

	const handleAmountChange = () => {
		const timeoutId = setTimeout(() => {
			handleApply();
		}, 10);

		return () => {
			clearTimeout(timeoutId);
		};
	};

	const handleToChange = () => {
		setTimeout(handleApply, 10);
	};

	const validateToValueOnBlur = (index) => {
		const currentValues = form.getFieldValue('grossRentList_new');
		if (!currentValues) return;

		const toValue = parseInt(currentValues[index]?.to);
		const fromValue = parseInt(currentValues[index]?.from);

		if (!isNaN(toValue) && !isNaN(fromValue) && toValue < fromValue) {
			message.error("'To' value must be greater than or equal to 'From' value");
			form.setFields([
				{
					name: ['grossRentList_new', index, 'to'],
					value: '',
					errors: ["'To' value must be greater than 'From' value"],
				},
			]);
		}

		setTimeout(handleApply, 10);
	};

	const isRowValid = (row) => {
		if (!row) return false;

		const toValue = parseInt(row.to);
		const fromValue = parseInt(row.from);

		if (isNaN(toValue) || toValue <= 0) return false;
		if (isNaN(fromValue) || fromValue <= 0) return false;
		if (toValue < fromValue) return false;
		if (!row.amount) return false;

		return true;
	};

	const handleAddRow = (index) => {
		const leaseType = form.getFieldValue('leaseType');
		const currentValues = form.getFieldValue('grossRentList_new');

		if (!currentValues || !currentValues.length) {
			form.setFieldValue('grossRentList_new', [{ from: 1, to: '', amount: '', remarks: '' }]);
			return;
		}

		const currentRow = currentValues[index];

		if (!isRowValid(currentRow)) {
			message.error('Please complete all required fields in the current row before adding a new one');
			return;
		}

		const toValue = parseInt(currentRow.to);

		if (leaseType === 'Operating Lease' && toValue >= 12) {
			message.error('Maximum periods taken');
			return;
		}
		const newFrom = toValue + 1;

		const newRow = { from: newFrom, to: '', amount: '', remarks: '' };

		const updatedValues = [...currentValues.slice(0, index + 1), newRow, ...currentValues.slice(index + 1)];

		for (let i = index + 2; i < updatedValues.length; i++) {
			const prevTo = parseInt(updatedValues[i - 1].to);
			if (!isNaN(prevTo)) {
				updatedValues[i].from = prevTo + 1;
			}
		}

		form.setFieldValue('grossRentList_new', updatedValues);

		setTimeout(handleApply, 10);
	};

	const handleApply = () => {
		const currentValues = form.getFieldValue('grossRentList_new');

		if (!currentValues || currentValues.length === 0) return;
		const transformedData = transformRangeDataToAmountArray(currentValues);

		form.setFieldValue('grossRents_new', transformedData);
		const currentAdvanceAmountsValues = form.getFieldValue('advanceAmountList_new');
		if (!currentAdvanceAmountsValues || currentAdvanceAmountsValues?.length === 0) return;

		const transformedDataAdvanceAmounts = transformRangeDataToAmountArray(currentAdvanceAmountsValues);

		const { processedAdvanceAmounts } = processArrays(transformedData, transformedDataAdvanceAmounts);

		form.setFieldValue('advanceAmounts_new', processedAdvanceAmounts);
		form.setFieldValue('leaseTerm_new', transformedData?.length);

		const leaseStartDate = form.getFieldValue('leaseStartDate');

		const leaseEndDate = leaseStartDate.add(transformedData?.length, 'month').subtract(1, 'day');

		form.setFieldValue('leaseEndDate_new', leaseEndDate);

		callDebounced();
	};

	useEffect(() => {
		const currentValues = form.getFieldValue('grossRentList_new');
		if (!currentValues || !currentValues.length) {
			form.setFieldValue('grossRentList_new', [{ from: 1, to: '', amount: '', remarks: '' }]);
		}
	}, [form]);

	const theme = useTheme();

	const { state } = useLocation();
	const [formState, setFormState] = useState({
		showActiveDeactiveModal: false,
		showFormSubmitModal: false,
		submitPayload: null,
		modalData: {
			head: state?.processName ?? '',
			label: 'Do you want to proceed?',
		},
	});
	const updateFormState = stateUpdater(setFormState);
	const [draftSubmit, { isSuccess: isDraftSubmitSuccess }] = useIdentityBulkMutation();
	const handleSubmitClick = () => {
		const payloadData = [viewData?._id];
		const formattedPayload = {
			_id: payloadData,
			operation: 'submit-draft-bulk',
			processCode: state?.processCode,
			suffixCode: '',
		};

		updateFormState('showFormSubmitModal', true);
		updateFormState('modalData.head', 'Confirmation');
		updateFormState('modalData.label', 'Do you want to submit this lease record?');

		updateFormState('submitPayload', formattedPayload);
	};
	const handleModalSubmit = async () => {
		await draftSubmit(formState?.submitPayload);
	};
	const setShowFormSubmitModal = (value) => {
		updateFormState('showFormSubmitModal', value);
	};
	const [getLeaseTableData, { data: leaseControlDataV3 }] = useGetQueryV3Mutation();
	const [IdentityBulk, { isSuccess: isCaseSuccess, isError: isCaseError }] = useIdentityBulkMutation();

	const [isDraft, setIsDraft] = useState(false);

	const navigate = useNavigate();

	const { creatorOrganizationId } = useSelector((state) => state.profile.profileData);
	const companyCode = creatorOrganizationId;
	const [getLeaseData, { isLoading: isLeaseControlDataLoading }] = useSendControlDataMutation();

	const [areAllRecordsRecorded, setAreAllRecordsRecorded] = useState(false);
	const [showClosedModal, setShowClosedModal] = useState(false);
	const [modalData, setModalData] = useState({
		head: state?.processName ?? '',
		label: 'Do you want to proceed?',
	});
	const [activeDeactiveSubmitData, setActiveDeactiveSubmitData] = useState(null);

	const modalSubmitHandler = () => {
		if (modalData?.action === 'Closed') {
			const payload = {
				_id: activeDeactiveSubmitData.map((item) => item._id),
				operation: 'activate-bulk',
				processCode: state?.processCode,
				activationStatus: 'Closed',
			};
			IdentityBulk(payload);
		} else if (modalData?.action === 'Reopened') {
			const payload = {
				_id: activeDeactiveSubmitData.map((item) => item._id),
				operation: 'activate-bulk',
				processCode: state?.processCode,
				activationStatus: 'Reopened',
			};
			IdentityBulk(payload);
		}
		setShowClosedModal(false);
	};

	useEffect(() => {
		if (isCaseSuccess) {
			toast.success('Success! Lease status updated successfully', toastConfig);
			navigate(-1);
		} else if (isCaseError) {
			toast.error('Failed! Status update not completed', toastConfig);
			setShowClosedModal(false);
		}
	}, [isCaseSuccess, isCaseError]);

	const queryV3Loader = async () => {
		if (screen !== 'CREATE') {
			const version = String(viewData?.data?.version);

			if (version && version !== 'null' && version !== 'undefined' && companyCode) {
				await getLeaseTableData({
					companyCode,
					fieldCodes: ['companyCode', 'identityRecordIdString', 'version'],
					fieldValues: [companyCode, state?._id, version],
					name: 'leaseSchedulesOnVersion-v9',
				});
			}
		}
	};
	useEffect(() => {
		if (isDraftSubmitSuccess) {
			navigate(-1);
		}
	}, [isDraftSubmitSuccess]);

	useEffect(() => {
		queryV3Loader();
		setIsDraft(viewData?.activationStatus?.toLowerCase() === 'draft');
	}, [dataFullyLoaded, viewData]);

	useEffect(() => {
		if (Array.isArray(leaseControlDataV3?.results)) {
			const isAllRecordsRecorded = leaseControlDataV3?.results?.every((item) => item?.processStatus === 'Recorded');

			setAreAllRecordsRecorded(isAllRecordsRecorded);
		}
	}, [JSON.stringify(leaseControlDataV3?.results)]);

	const [rowTotals, setRowTotals] = useState([]);
	const [totalAmount, setTotalAmount] = useState(0);

	const formAdvanceAmount = Form.useWatch(constantFormValues?.advanceAmount?.key, form);

	useEffect(() => {
		if (formAdvanceAmount !== undefined && formAdvanceAmount !== null) {
			if (formAdvanceAmount !== totalAmount) {
				setTotalAmount(formAdvanceAmount);
			}
		}
	}, [formAdvanceAmount]);

	useEffect(() => {
		if (totalAmount > 0) {
			const currentFormValue = form.getFieldValue(constantFormValues?.advanceAmount?.key);

			if (currentFormValue !== totalAmount) {
				form.setFieldValue(constantFormValues?.advanceAmount?.key, totalAmount);
			}
		}
	}, [totalAmount]);

	function transformRangeDataToAmountArray(rangeData) {
		const result = [];

		rangeData.forEach((range) => {
			const fromValue = parseInt(range.from);
			const toValue = parseInt(range.to);

			if (isNaN(fromValue) || isNaN(toValue) || fromValue > toValue) {
				console.warn('Invalid range found:', range);
				return;
			}

			const amount = parseFloat(String(range.amount).replace(/,/g, ''));

			if (isNaN(amount)) {
				console.warn('Invalid amount found:', range);
				return;
			}

			const repetitions = toValue - fromValue + 1;

			for (let i = 0; i < repetitions; i++) {
				result.push(amount);
			}
		});

		return result;
	}

	const calculateRowTotals = () => {
		// if (!form || !formListName) return;

		try {
			// Get the current values from the form
			const values = form.getFieldValue('advanceAmountList');

			if (values && Array.isArray(values)) {
				const newRowTotals = values.map((item) => {
					if (!item) return 0;

					const fromValue = parseInt(item.from);
					const toValue = parseInt(item.to);

					// If amount exists, convert it from string to number
					let amount = 0;
					if (item.amount) {
						const amountStr = String(item.amount).replace(/,/g, '');
						amount = parseFloat(amountStr);
					}

					// Skip invalid values
					if (isNaN(fromValue) || isNaN(toValue) || isNaN(amount) || toValue < fromValue) {
						return 0;
					}

					// Calculate total: (to - from + 1) * amount
					return (toValue - fromValue + 1) * amount;
				});

				setRowTotals(newRowTotals);
			}
		} catch (error) {
			console.error('Error calculating row totals:', error);
		}
	};

	const calculateTotal = () => {
		try {
			// Get the current values from the form
			const values = form.getFieldValue('advanceAmountList');

			if (values && Array.isArray(values)) {
				// Calculate row totals directly without waiting for state update
				const currentRowTotals = values.map((item) => {
					if (!item) return 0;

					const fromValue = parseInt(item.from);
					const toValue = parseInt(item.to);

					// If amount exists, convert it from string to number
					let amount = 0;
					if (item.amount !== undefined && item.amount !== null && item.amount !== '') {
						const amountStr = String(item.amount).replace(/,/g, '');
						amount = parseFloat(amountStr);
					}

					// Skip invalid values
					if (isNaN(fromValue) || isNaN(toValue) || isNaN(amount) || toValue < fromValue) {
						return 0;
					}

					// Calculate total: (to - from + 1) * amount
					return (toValue - fromValue + 1) * amount;
				});

				// Update row totals state
				setRowTotals(currentRowTotals);

				// Now calculate the grand total using the current row totals
				const sum = currentRowTotals.reduce((total, rowTotal) => total + (rowTotal || 0), 0);

				// Update total amount state
				setTotalAmount(sum);
			}
		} catch (error) {
			console.error('Error calculating total:', error);
		}
	};

	useEffect(() => {
		if (screen === 'VIEW' && dataFullyLoaded) {
			const timeoutId = setTimeout(() => {
				const currentValues = form.getFieldValue('advanceAmountList');

				if (currentValues && currentValues.length > 0) {
					calculateTotal();
				}
			}, 700);

			return () => clearTimeout(timeoutId);
		}
	}, [screen, dataFullyLoaded]);

	useEffect(() => {
		const currentValues = form.getFieldValue('advanceAmountList');

		if (!currentValues || !currentValues.length) {
			form.setFieldValue('advanceAmountList', [{ from: 1, to: '', amount: '', totalAmount: '' }]);
		}

		calculateTotal();
		calculateRowTotals();
	}, []);

	useEffect(() => {
		if (totalAmount > 0) {
			form.setFieldValue(constantFormValues?.advanceAmount?.key, totalAmount);

			const currentChanges = form.getFieldValue('changes');

			if (currentChanges && currentChanges.length > 0) {
				const updatedChanges = [...currentChanges];
				updatedChanges[0] = {
					...updatedChanges[0],
					inputChangeAdvance: totalAmount,
				};
			}
		}
	}, [totalAmount]);

	const { maxRecordedDate } = useSelector((state) => state.lease);

	const handleInputMonthChange = (e, index) => {
		if (!e) {
			const currentChanges = form.getFieldValue('changes') || [];
			if (currentChanges[index]) {
				const updatedChanges = [...currentChanges];
				updatedChanges[index] = {
					...updatedChanges[index],
					inputChangeMonth: null,
					inputChangeEffectiveDate: null,
				};
				form.setFieldsValue({ changes: updatedChanges });
			}
			return;
		}
		const effectiveDate = dayjs(e);
		const leaseStart = dayjs(form.getFieldValue('leaseStartDate'));
		let monthDifference = (effectiveDate.year() - leaseStart.year()) * 12 + (effectiveDate.month() - leaseStart.month());
		monthDifference += 1;
		if (monthDifference < 0) {
			monthDifference = 0;
		}
		const currentChanges = form.getFieldValue('changes') || [];
		if (currentChanges[index]) {
			const updatedChanges = [...currentChanges];
			updatedChanges[index] = {
				...updatedChanges[index],
				inputChangeMonth: monthDifference,
				inputChangeEffectiveDate: utcConverter(effectiveDate),
			};
			form.setFieldsValue({ changes: updatedChanges });
		}
	};

	const getMaxDateForRow = (index) => {
		if (index === 0) {
			return null;
		}
		const leaseStartDate = dayjs(form.getFieldValue('leaseStartDate'));
		if (!leaseStartDate) return null;
		const changes = form.getFieldValue('changes');
		if (!changes || !changes[index - 1]) return null;
		const prevRowLeaseTerm = changes[index - 1].inputChangeLeaseTerm;
		if (!prevRowLeaseTerm) return null;
		return leaseStartDate.add(parseInt(prevRowLeaseTerm), 'month').subtract(1, 'day');
	};

	return (
		<>
			<Typography
				variant="h4"
				sx={{ color: '#1a3b5d', padding: '0 0 12px 0', fontWeight: 600, fontSize: '18px', borderBottom: '1px solid #e0e6ed' }}
			>
				Basic Information
			</Typography>

			<Row gutter={[16, 0]} style={{ marginTop: '16px' }}>
				<Form.Item noStyle name="tabIndex" />
				<Form.Item noStyle name="endOfFinancialYear" />
				<Form.Item noStyle name={constantFormValues?.version?.key} />
				<Form.Item noStyle name={constantFormValues?.leaseTerm?.key} />
				<Form.Item noStyle name={constantFormValues?.lessorName?.key} />
				<Form.Item noStyle name={constantFormValues?.creationDate?.key} />
				<Form.Item noStyle name={constantFormValues?.paymentModality?.key} />
				<Form.Item noStyle name={constantFormValues?.lastModifiedDate?.key} />
				<Form.Item noStyle name={`${constantFormValues?.lessorName?.key}_new`} />
				<Form.Item noStyle name={constantFormValues?.paymentModalitylabel?.key} />

				<Col xs={24} md={6}>
					<InputLabel label={constantFormValues?.LeaseId?.label} head={true} />
				</Col>
				<Col xs={24} md={9}>
					<Form.Item name={constantFormValues?.LeaseId?.key}>
						<Input disabled placeholder="To be auto generated" style={{ height: '32px', borderRadius: '4px' }} />
					</Form.Item>
				</Col>
				<Col xs={24} md={9}>
					<Form.Item name={constantFormValues?.LeaseId?.key}>
						<Input disabled placeholder="To be auto generated" style={{ height: '32px', borderRadius: '4px' }} />
					</Form.Item>
				</Col>

				<Col xs={24} md={6}>
					<InputLabel label={constantFormValues?.leaseTitle?.label} head={true} />
				</Col>
				<Col xs={24} md={9}>
					<Form.Item name={constantFormValues?.leaseTitle?.key} rules={constantFormValues?.leaseTitle?.rules}>
						<Input disabled placeholder="Write here" style={{ height: '32px', borderRadius: '4px' }} />
					</Form.Item>
				</Col>
				<Col xs={24} md={9}>
					<Form.Item name={`${constantFormValues?.leaseTitle?.key}_new`} rules={constantFormValues?.leaseTitle?.rules}>
						<Input placeholder="Write here" style={{ height: '32px', borderRadius: '4px' }} />
					</Form.Item>
				</Col>

				<Col xs={24} md={6}>
					<InputLabel label={constantFormValues?.lessorID?.label} head={true} />
				</Col>
				<Col xs={24} md={9}>
					<Form.Item name={constantFormValues?.lessorID?.key} rules={constantFormValues?.lessorID?.rules}>
						<Select
							showSearch
							disabled={true}
							filterOption={true}
							optionFilterProp="children"
							placeholder="Select from the list"
							style={{ width: '100%', height: '32px', borderRadius: '4px' }}
							onSelect={(_, selectItem) => form.setFieldValue(constantFormValues?.lessorName?.key, selectItem?.children)}
						>
							{vendorList?.map(({ label, value }) => (
								<Select.Option key={value} value={value}>
									{label}
								</Select.Option>
							))}
						</Select>
					</Form.Item>
				</Col>
				<Col xs={24} md={9}>
					<Form.Item name={constantFormValues?.lessorID?.key} rules={constantFormValues?.lessorID?.rules}>
						<Select
							showSearch
							disabled={true}
							filterOption={true}
							optionFilterProp="children"
							placeholder="Select from the list"
							style={{ width: '100%', height: '32px', borderRadius: '4px' }}
							onSelect={(_, selectItem) => form.setFieldValue(`${constantFormValues?.lessorName?.key}_new`, selectItem?.children)}
						>
							{vendorList?.map(({ label, value }) => (
								<Select.Option key={value} value={value}>
									{label}
								</Select.Option>
							))}
						</Select>
					</Form.Item>
				</Col>

				<Col xs={24} md={6}>
					<InputLabel label={constantFormValues?.leaseStartDate?.label} head={true} />
				</Col>
				<Col xs={24} md={9}>
					<Form.Item name={constantFormValues?.leaseStartDate?.key} rules={constantFormValues?.leaseStartDate?.rules}>
						<DatePicker disabled format="YYYY-MM-DD" style={{ width: '100%', height: '32px', borderRadius: '4px' }} />
					</Form.Item>
				</Col>
				<Col xs={24} md={9}>
					<Form.Item name={constantFormValues?.leaseStartDate?.key} rules={constantFormValues?.leaseStartDate?.rules}>
						<DatePicker disabled format="YYYY-MM-DD" style={{ width: '100%', height: '32px', borderRadius: '4px' }} />
					</Form.Item>
				</Col>

				<Col xs={24} md={6}>
					<InputLabel label={constantFormValues?.leaseEndDate?.label} head={true} />
				</Col>
				<Col xs={24} md={9}>
					<Form.Item name={constantFormValues?.leaseEndDate?.key} rules={constantFormValues?.leaseEndDate?.rules}>
						<DatePicker disabled value={LeaseEndDate} style={{ width: '100%', height: '32px', borderRadius: '4px' }} />
					</Form.Item>
				</Col>
				<Col xs={24} md={9}>
					<Form.Item name={`${constantFormValues?.leaseEndDate?.key}_new`} rules={constantFormValues?.leaseEndDate?.rules}>
						<DatePicker disabled value={LeaseEndDate} style={{ width: '100%', height: '32px', borderRadius: '4px' }} />
					</Form.Item>
				</Col>

				<Col xs={24} md={6}>
					<InputLabel label={constantFormValues?.leaseTerm?.label} head={true} />
				</Col>
				<Col xs={24} md={9}>
					<Form.Item name="modifiedLeaseTerm" rules={constantFormValues?.leaseTerm?.rules} noStyle>
						<Input disabled type="number" placeholder="Write here" style={{ height: '32px', borderRadius: '4px' }} />
					</Form.Item>
				</Col>
				<Col xs={24} md={9}>
					<Form.Item name="leaseTerm_new" rules={constantFormValues?.leaseTerm?.rules} noStyle>
						<Input type="number" disabled placeholder="Write here" style={{ height: '32px', borderRadius: '4px' }} />
					</Form.Item>
				</Col>

				<Col xs={24} md={6}>
					<InputLabel label={constantFormValues?.leaseType?.label} head={true} />
				</Col>
				<Col xs={24} md={9}>
					<Form.Item name={constantFormValues?.leaseType?.key} rules={constantFormValues?.leaseType?.rules}>
						<Select
							disabled
							showSearch
							filterOption={true}
							optionFilterProp="children"
							placeholder="Select from the list"
							style={{ width: '100%', height: '32px', borderRadius: '4px' }}
							onSelect={(value) => {
								if (value === 'Operating Lease') {
									form.setFieldValue('grossRentList', [{ from: 1, to: '', amount: '', remarks: '' }]);
									form.setFieldValue('advanceAmountList', [
										{
											from: 1,
											to: '',
											amount: '',
											totalAmount: '',
										},
									]);
								}
							}}
						>
							{constantFormValues?.leaseType?.options?.map(({ key, value, label }) => (
								<Select.Option key={key} value={value}>
									{label}
								</Select.Option>
							))}
						</Select>
					</Form.Item>
				</Col>
				<Col xs={24} md={9}>
					<Form.Item name={`${constantFormValues?.leaseType?.key}`} rules={constantFormValues?.leaseType?.rules}>
						<Select
							disabled
							showSearch
							filterOption={true}
							optionFilterProp="children"
							placeholder="Select from the list"
							style={{ width: '100%', height: '32px', borderRadius: '4px' }}
							onSelect={(value) => {
								if (value === 'Operating Lease') {
									form.setFieldValue('grossRentList', [{ from: 1, to: '', amount: '', remarks: '' }]);
									form.setFieldValue('advanceAmountList', [
										{
											from: 1,
											to: '',
											amount: '',
											totalAmount: '',
										},
									]);
								}
							}}
						>
							{constantFormValues?.leaseType?.options?.map(({ key, value, label }) => (
								<Select.Option key={key} value={value}>
									{label}
								</Select.Option>
							))}
						</Select>
					</Form.Item>
				</Col>

				<Col xs={24}>
					<Form.Item
						shouldUpdate={(previousValue, currentValue) =>
							previousValue?.[constantFormValues?.leaseType?.key] !== currentValue?.[constantFormValues?.leaseType?.key]
						}
						noStyle
					>
						{({ getFieldValue }) => {
							if (getFieldValue(constantFormValues?.leaseType?.key) === 'Finance Lease') {
								return (
									<Row gutter={[16, 0]}>
										<Col xs={24} md={6}>
											<InputLabel label={constantFormValues?.initialDiscountRate?.label} head={true} />
										</Col>
										<Col xs={24} md={9}>
											<Form.Item
												name={constantFormValues?.initialDiscountRate?.key}
												rules={constantFormValues?.initialDiscountRate?.rules}
											>
												<Input disabled placeholder="Write here" style={{ height: '32px', borderRadius: '4px' }} />
											</Form.Item>
										</Col>
										<Col xs={24} md={9}>
											<Form.Item
												rules={constantFormValues?.initialDiscountRate?.rules}
												name={`${constantFormValues?.initialDiscountRate?.key}_new`}
											>
												<Input
													placeholder="Write here"
													style={{ height: '32px', borderRadius: '4px' }}
													onChange={(e) => {
														const value = e.target.value;
														// const currentChanges = form.getFieldValue('changes');

														form.setFieldValue(`${constantFormValues?.initialDiscountRate?.key}_new`, value);

														// form.setFieldValue(
														// 	['changes', currentChanges.length - 1, 'inputChangeDiscountRate'],
														// 	value
														// );

														callDebounced();
													}}
												/>
											</Form.Item>
										</Col>
									</Row>
								);
							} else {
								return <Form.Item name={constantFormValues?.initialDiscountRate?.key} noStyle />;
							}
						}}
					</Form.Item>
				</Col>
			</Row>

			<Row gutter={[16, 0]}>
				<Col xs={24} md={12}>
					<Typography
						variant="h4"
						sx={{
							color: '#1a3b5d',
							padding: '0 0 12px 0',
							fontWeight: 600,
							fontSize: '18px',
							borderBottom: '1px solid #e0e6ed',
							marginBottom: '16px',
						}}
					>
						Lease Payment Schedule
					</Typography>

					<Form.Item name="changes" noStyle />

					<Form.Item name="identityId" noStyle />
					<Form.Item name="grossRentListSet" noStyle />
					<Form.Item name="effectiveDateHistory" noStyle />
					<Form.Item name="modificationSequence" noStyle />

					<Form.List name={'grossRentList'}>
						{(fields) => (
							<>
								<TableContainer
									className="gross-rent-table"
									sx={{
										'& table, & th, & td': {
											border: '1px solid #e0e0e0',
											borderCollapse: 'collapse',
										},
										'& th, & td': {
											p: '5px',
										},
										'& td .ant-form-item': {
											marginBottom: '0px',
										},

										'& th:nth-of-type(1), & td:nth-of-type(1)': { width: '30%' },
										'& th:nth-of-type(2), & td:nth-of-type(2)': { width: '30%' },
										'& th:nth-of-type(3), & td:nth-of-type(3)': { width: '30%' },

										'& table': { width: '100%' },
									}}
								>
									<Table aria-label="Gross Rent table">
										<TableHead>
											<TableRow>
												<TableCell sx={{ fontSize: '13px', backgroundColor: '#f6f5f5', textAlign: 'center' }}>
													From Month
												</TableCell>
												<TableCell sx={{ fontSize: '13px', backgroundColor: '#f6f5f5', textAlign: 'center' }}>
													To Month
												</TableCell>
												<TableCell sx={{ fontSize: '13px', backgroundColor: '#f6f5f5', textAlign: 'center' }}>
													Amount
												</TableCell>
											</TableRow>
										</TableHead>

										<TableBody>
											{fields.length > 0 ? (
												fields.map((field, index) => (
													<TableRow key={field.key}>
														<TableCell>
															<Form.Item name={[field.name, 'from']}>
																<Input
																	disabled={true}
																	style={{
																		width: '100%',
																		padding: '5px 11px',
																		textAlign: 'center',
																		backgroundColor: '#f5f5f5',
																	}}
																/>
															</Form.Item>
														</TableCell>

														<TableCell>
															<Form.Item name={[field.name, 'to']}>
																<Input
																	disabled
																	type="number"
																	placeholder="Enter To value"
																	style={{
																		width: '100%',
																		padding: '5px 11px',
																		textAlign: 'center',
																	}}
																/>
															</Form.Item>
														</TableCell>

														<TableCell>
															<Form.Item name={[field.name, 'amount']}>
																<NumericFormat
																	size="small"
																	disabled={true}
																	decimalScale={2}
																	placeholder="0.00"
																	fixedDecimalScale
																	thousandSeparator=","
																	customInput={Input}
																	isAllowed={withValueCap}
																	style={{
																		height: '32px',
																		padding: '5px 11px',
																		fontSize: '13px',
																		textAlign: 'right',
																		borderRadius: '4px',
																	}}
																/>
															</Form.Item>
														</TableCell>
													</TableRow>
												))
											) : (
												<TableRow>
													<TableCell colSpan={5} style={{ textAlign: 'center' }}>
														No data. Click plus icon in action column to add new entries.
													</TableCell>
												</TableRow>
											)}
										</TableBody>
									</Table>
								</TableContainer>

								<Form.Item name="grossRents" noStyle />
							</>
						)}
					</Form.List>

					<Row gutter={[16, 0]} style={{ marginTop: '16px' }}>
						<Col xs={24} md={12}>
							<Form.Item name={constantFormValues?.paymentFrequency?.key} label={constantFormValues?.paymentFrequency?.label}>
								<Select
									disabled
									showSearch
									filterOption={true}
									optionFilterProp="children"
									placeholder="Select from the list"
									style={{ width: '100%', height: '32px', borderRadius: '4px' }}
								>
									{constantFormValues?.paymentFrequency?.options?.map(({ key, value, label }) => (
										<Select.Option key={key} value={value}>
											{label}
										</Select.Option>
									))}
								</Select>
							</Form.Item>
						</Col>

						<Col xs={24} md={12}>
							<Form.Item name={constantFormValues?.paymentModality?.key} label={constantFormValues?.paymentModality?.label}>
								<Select
									disabled
									showSearch
									filterOption={true}
									optionFilterProp="children"
									placeholder="Select from the list"
									style={{ width: '100%', height: '32px', borderRadius: '4px' }}
								>
									{constantFormValues?.paymentModality?.options?.map(({ key, value, label }) => (
										<Select.Option key={key} value={value}>
											{label}
										</Select.Option>
									))}
								</Select>
							</Form.Item>
						</Col>
					</Row>
				</Col>

				<Col xs={24} md={12}>
					<Typography
						variant="h4"
						sx={{
							color: '#1a3b5d',
							padding: '0 0 12px 0',
							fontWeight: 600,
							fontSize: '18px',
							borderBottom: '1px solid #e0e6ed',
							marginBottom: '16px',
						}}
					>
						Lease Payment Schedule
					</Typography>

					<Form.Item name="identityId" noStyle />
					<Form.Item name="grossRentListSet" noStyle />
					<Form.Item name="effectiveDateHistory" noStyle />
					<Form.Item name="modificationSequence" noStyle />

					<Form.List name={'grossRentList_new'}>
						{(fields) => (
							<>
								<TableContainer
									className="gross-rent-table"
									sx={{
										'& table, & th, & td': {
											border: '1px solid #e0e0e0',
											borderCollapse: 'collapse',
										},
										'& th, & td': {
											p: '5px',
										},
										'& td .ant-form-item': {
											marginBottom: '0px',
										},

										'& th:nth-of-type(1), & td:nth-of-type(1)': { width: '30%' },
										'& th:nth-of-type(2), & td:nth-of-type(2)': { width: '30%' },
										'& th:nth-of-type(3), & td:nth-of-type(3)': { width: '30%' },

										'& th:nth-of-type(4), & td:nth-of-type(4)': { width: '10%' },

										'& table': { width: '100%' },
									}}
								>
									<Table aria-label="Gross Rent table">
										<TableHead>
											<TableRow>
												<TableCell sx={{ fontSize: '13px', backgroundColor: '#f6f5f5', textAlign: 'center' }}>
													From Month
												</TableCell>
												<TableCell sx={{ fontSize: '13px', backgroundColor: '#f6f5f5', textAlign: 'center' }}>
													To Month
												</TableCell>
												<TableCell sx={{ fontSize: '13px', backgroundColor: '#f6f5f5', textAlign: 'center' }}>
													Amount
												</TableCell>
												<TableCell sx={{ fontSize: '13px', backgroundColor: '#f6f5f5', textAlign: 'center' }}>
													Action
												</TableCell>
											</TableRow>
										</TableHead>

										<TableBody>
											{fields.length > 0 ? (
												fields.map((field, index) => (
													<TableRow key={field.key}>
														<TableCell>
															<Form.Item name={[field.name, 'from']}>
																<Input
																	disabled={true}
																	style={{
																		width: '100%',
																		padding: '5px 11px',
																		textAlign: 'center',
																		backgroundColor: '#f5f5f5',
																	}}
																/>
															</Form.Item>
														</TableCell>

														<TableCell>
															<Form.Item
																name={[field.name, 'to']}
																rules={[
																	{ required: true, message: "Please enter a 'To' value" },
																	({ getFieldValue }) => ({
																		validator(_, value) {
																			const leaseType = getFieldValue('leaseType');
																			const fromValue = getFieldValue([
																				'grossRentList_new',
																				field.name,
																				'from',
																			]);
																			if (!value || !fromValue) return Promise.resolve();
																			if (parseInt(value) < parseInt(fromValue)) {
																				return Promise.reject(new Error("'To' must be greater than 'From'"));
																			}
																			if (leaseType === 'Operating Lease' && parseInt(value) > 12) {
																				return Promise.reject(new Error('Maximum month is 12'));
																			}
																			return Promise.resolve();
																		},
																	}),
																]}
															>
																<Input
																	type="number"
																	placeholder="Enter To value"
																	onChange={() => handleToChange()}
																	onBlur={() => validateToValueOnBlur(index)}
																	style={{
																		width: '100%',
																		padding: '5px 11px',

																		textAlign: 'center',
																	}}
																/>
															</Form.Item>
														</TableCell>
														<TableCell>
															<Form.Item
																name={[field.name, 'amount']}
																rules={[{ required: true, message: 'Please enter an amount' }]}
															>
																<NumericFormat
																	size="small"
																	decimalScale={2}
																	placeholder="0.00"
																	fixedDecimalScale
																	thousandSeparator=","
																	isAllowed={withValueCap}
																	customInput={Input}
																	disabled={screen === 'VIEW'}
																	onBlur={() => setTimeout(handleApply, 10)}
																	style={{
																		height: '32px',
																		padding: '5px 11px',
																		fontSize: '13px',
																		textAlign: 'right',
																		borderRadius: '4px',
																	}}
																	onValueChange={(values) => {
																		handleAmountChange(`${'grossRentList_new'}.${field.name}.amount`);
																		const currentValues = [...form.getFieldValue('grossRentList_new')];

																		if (currentValues[index]) {
																			currentValues[index].amount = values.value || '';

																			form.setFieldValue('grossRentList_new', currentValues);
																		}
																	}}
																/>
															</Form.Item>
														</TableCell>

														<TableCell align="center">
															<Stack sx={{ flexDirection: 'row', justifyContent: 'center' }}>
																{fields.length === 1 && index === 0 && (
																	<Tooltip title="Add Row">
																		<AddIconButton onClick={() => handleAddRow(index)} />
																	</Tooltip>
																)}

																{fields.length > 1 && index > 0 && index < fields.length - 1 && (
																	<Tooltip title="Delete Row">
																		<DeleteIconButton
																			onClick={() => {
																				const currentValues = [...form.getFieldValue('grossRentList_new')];

																				currentValues.splice(index, 1);

																				for (let i = index; i < currentValues.length; i++) {
																					if (i > 0) {
																						const prevTo = parseInt(currentValues[i - 1].to);
																						if (!isNaN(prevTo)) {
																							currentValues[i].from = prevTo + 1;
																						}
																					}
																				}

																				form.setFieldValue('grossRentList_new', currentValues);

																				setTimeout(handleApply, 10);
																			}}
																		/>
																	</Tooltip>
																)}

																{fields.length > 1 && index === fields.length - 1 && (
																	<>
																		<Tooltip title="Add Row">
																			<AddIconButton onClick={() => handleAddRow(index)} />
																		</Tooltip>

																		<Tooltip title="Delete Row">
																			<DeleteIconButton
																				onClick={() => {
																					const currentValues = [
																						...form.getFieldValue('grossRentList_new'),
																					];

																					currentValues.splice(index, 1);

																					for (let i = index; i < currentValues.length; i++) {
																						if (i > 0) {
																							const prevTo = parseInt(currentValues[i - 1].to);
																							if (!isNaN(prevTo)) {
																								currentValues[i].from = prevTo + 1;
																							}
																						}
																					}

																					form.setFieldValue('grossRentList_new', currentValues);

																					setTimeout(handleApply, 10);
																				}}
																			/>
																		</Tooltip>
																	</>
																)}

																{fields.length > 1 && index === 0 && <span></span>}
															</Stack>
														</TableCell>
													</TableRow>
												))
											) : (
												<TableRow>
													<TableCell colSpan={5} style={{ textAlign: 'center' }}>
														No data. Click plus icon in action column to add new entries.
													</TableCell>
												</TableRow>
											)}
										</TableBody>
									</Table>
								</TableContainer>

								<Form.Item name="grossRents_new" noStyle />
							</>
						)}
					</Form.List>

					<Row gutter={[16, 0]} style={{ marginTop: '16px' }}>
						<Col xs={24} md={12}>
							<Form.Item
								getValueFromEvent={handlePaymentFrequencyChange}
								label={constantFormValues?.paymentFrequency?.label}
								rules={constantFormValues?.paymentFrequency?.rules}
								name={`${constantFormValues?.paymentFrequency?.key}_new`}
							>
								<Select
									showSearch
									filterOption={true}
									optionFilterProp="children"
									placeholder="Select from the list"
									onBlur={handlePaymentFrequencyBlur}
									style={{ width: '100%', height: '32px', borderRadius: '4px' }}
								>
									{constantFormValues?.paymentFrequency?.options?.map(({ key, value, label }) => (
										<Select.Option key={key} value={value}>
											{label}
										</Select.Option>
									))}
								</Select>
							</Form.Item>
						</Col>

						<Col xs={24} md={12}>
							<Form.Item
								getValueFromEvent={handlePaymentModalityChange}
								rules={constantFormValues?.paymentModality?.rules}
								label={constantFormValues?.paymentModality?.label}
								name={`${constantFormValues?.paymentModality?.key}_new`}
							>
								<Select
									showSearch
									filterOption={true}
									optionFilterProp="children"
									onBlur={handlePaymentModalityBlur}
									placeholder="Select from the list"
									style={{ width: '100%', height: '32px', borderRadius: '4px' }}
								>
									{constantFormValues?.paymentModality?.options?.map(({ key, value, label }) => (
										<Select.Option key={key} value={value}>
											{label}
										</Select.Option>
									))}
								</Select>
							</Form.Item>
						</Col>
					</Row>
				</Col>

				<Col xs={24} md={12}>
					<Typography
						variant="h4"
						sx={{
							color: '#1a3b5d',
							padding: '0 0 12px 0',
							fontWeight: 600,
							fontSize: '18px',
							borderBottom: '1px solid #e0e6ed',
							marginBottom: '16px',
						}}
					>
						Advance Payment & Adjustment
					</Typography>

					<Form.Item name="advanceAmountListSet" noStyle />

					<Form.List name={'advanceAmountList'}>
						{(fields) => {
							return (
								<>
									<TableContainer
										className="gross-rent-table"
										sx={{
											'& table, & th, & td': {
												border: '1px solid #e0e0e0',
												borderCollapse: 'collapse',
											},
											'& th, & td': {
												p: '5px',
											},
											'& td .ant-form-item': {
												marginBottom: '0px',
											},

											'& th:nth-of-type(1), & td:nth-of-type(1)': { width: '30%' },
											'& th:nth-of-type(2), & td:nth-of-type(2)': { width: '30%' },
											'& th:nth-of-type(3), & td:nth-of-type(3)': { width: '30%' },

											'& table': { width: '100%' },
										}}
									>
										<Table aria-label="Gross Rent table">
											<TableHead>
												<TableRow>
													<TableCell sx={{ fontSize: '13px', backgroundColor: '#f6f5f5', textAlign: 'center' }}>
														From Month
													</TableCell>
													<TableCell sx={{ fontSize: '13px', backgroundColor: '#f6f5f5', textAlign: 'center' }}>
														To Month
													</TableCell>
													<TableCell sx={{ fontSize: '13px', backgroundColor: '#f6f5f5', textAlign: 'center' }}>
														Monthly Adjustment
													</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{fields.length > 0 ? (
													fields.map((field, index) => (
														<TableRow key={field.key}>
															<TableCell>
																<Form.Item name={[field.name, 'from']}>
																	<Input
																		disabled={true}
																		style={{
																			width: '100%',
																			padding: '5px 11px',
																			textAlign: 'center',
																			backgroundColor: '#f5f5f5',
																		}}
																	/>
																</Form.Item>
															</TableCell>

															<TableCell>
																<Form.Item name={[field.name, 'to']}>
																	<Input
																		disabled
																		type="number"
																		placeholder="Enter To value"
																		style={{
																			padding: '5px 11px',
																			width: '100%',

																			textAlign: 'center',
																		}}
																	/>
																</Form.Item>
															</TableCell>
															<TableCell>
																<Form.Item name={[field.name, 'amount']}>
																	<NumericFormat
																		disabled
																		size="small"
																		decimalScale={2}
																		placeholder="0.00"
																		fixedDecimalScale
																		thousandSeparator=","
																		isAllowed={withValueCap}
																		style={{
																			padding: '5px 11px',
																			textAlign: 'right',
																			height: '32px',
																			borderRadius: '4px',
																			fontSize: '13px',
																		}}
																		customInput={Input}
																	/>
																</Form.Item>
															</TableCell>
														</TableRow>
													))
												) : (
													<TableRow>
														<TableCell colSpan={5} style={{ textAlign: 'center' }}>
															No data. Click the plus icon to add new entries.
														</TableCell>
													</TableRow>
												)}
											</TableBody>
											<TableFooter>
												<TableRow sx={{ height: 24 }}>
													<TableCell
														colSpan={2}
														align="right"
														sx={{ fontWeight: 'bold', fontSize: '13px', backgroundColor: '#f6f5f5', pr: '14px' }}
													>
														Advance Payment
													</TableCell>

													<TableCell style={{ fontWeight: 'bold', textAlign: 'right' }}>
														<NumericFormat
															value={totalAmount || 0}
															displayType="input"
															decimalScale={2}
															fixedDecimalScale
															thousandSeparator=","
															size="small"
															style={{
																padding: '5px 11px',
																textAlign: 'right',
																height: '32px',
																borderRadius: '4px',
																fontSize: '13px',
																fontWeight: 'bold',
																backgroundColor: undefined,
																borderColor: undefined,
															}}
															customInput={Input}
															disabled={true}
															readOnly={true}
														/>
													</TableCell>
												</TableRow>
											</TableFooter>
										</Table>
									</TableContainer>

									<Form.Item name="advanceAmounts" noStyle />
								</>
							);
						}}
					</Form.List>
				</Col>

				<Col xs={24} md={12}>
					<Typography
						variant="h4"
						sx={{
							color: '#1a3b5d',
							padding: '0 0 12px 0',
							fontWeight: 600,
							fontSize: '18px',
							borderBottom: '1px solid #e0e6ed',
							marginBottom: '16px',
						}}
					>
						Advance Payment & Adjustment
					</Typography>

					<Form.Item name="advanceAmountListSet" noStyle />

					<Form.List name={'advanceAmountList'}>
						{(fields) => {
							return (
								<>
									<TableContainer
										className="gross-rent-table"
										sx={{
											'& table, & th, & td': {
												border: '1px solid #e0e0e0',
												borderCollapse: 'collapse',
											},
											'& th, & td': {
												p: '5px',
											},
											'& td .ant-form-item': {
												marginBottom: '0px',
											},

											'& th:nth-of-type(1), & td:nth-of-type(1)': { width: '30%' },
											'& th:nth-of-type(2), & td:nth-of-type(2)': { width: '30%' },
											'& th:nth-of-type(3), & td:nth-of-type(3)': { width: '30%' },

											'& table': { width: '100%' },
										}}
									>
										<Table aria-label="Gross Rent table">
											<TableHead>
												<TableRow>
													<TableCell sx={{ fontSize: '13px', backgroundColor: '#f6f5f5', textAlign: 'center' }}>
														From Month
													</TableCell>
													<TableCell sx={{ fontSize: '13px', backgroundColor: '#f6f5f5', textAlign: 'center' }}>
														To Month
													</TableCell>
													<TableCell sx={{ fontSize: '13px', backgroundColor: '#f6f5f5', textAlign: 'center' }}>
														Monthly Adjustment
													</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{fields.length > 0 ? (
													fields.map((field, index) => (
														<TableRow key={field.key}>
															<TableCell>
																<Form.Item name={[field.name, 'from']}>
																	<Input
																		disabled={true}
																		style={{
																			padding: '5px 11px',
																			width: '100%',
																			backgroundColor: '#f5f5f5',
																			textAlign: 'center',
																		}}
																	/>
																</Form.Item>
															</TableCell>

															<TableCell>
																<Form.Item name={[field.name, 'to']}>
																	<Input
																		disabled
																		type="number"
																		placeholder="Enter To value"
																		style={{
																			padding: '5px 11px',
																			width: '100%',

																			textAlign: 'center',
																		}}
																	/>
																</Form.Item>
															</TableCell>

															<TableCell>
																<Form.Item
																	name={[field.name, 'amount']}
																	rules={[{ required: false, message: 'Please enter an amount' }]}
																>
																	<NumericFormat
																		disabled
																		size="small"
																		decimalScale={2}
																		placeholder="0.00"
																		fixedDecimalScale
																		thousandSeparator=","
																		isAllowed={withValueCap}
																		style={{
																			padding: '5px 11px',
																			textAlign: 'right',
																			height: '32px',
																			borderRadius: '4px',
																			fontSize: '13px',
																		}}
																		customInput={Input}
																	/>
																</Form.Item>
															</TableCell>
														</TableRow>
													))
												) : (
													<TableRow>
														<TableCell colSpan={5} style={{ textAlign: 'center' }}>
															No data. Click the plus icon to add new entries.
														</TableCell>
													</TableRow>
												)}
											</TableBody>

											<TableFooter>
												<TableRow sx={{ height: 24 }}>
													<TableCell
														colSpan={2}
														align="right"
														sx={{ fontWeight: 'bold', fontSize: '13px', backgroundColor: '#f6f5f5', pr: '14px' }}
													>
														Advance Payment
													</TableCell>

													<TableCell style={{ fontWeight: 'bold', textAlign: 'right' }}>
														<NumericFormat
															value={totalAmount || 0}
															displayType="input"
															decimalScale={2}
															fixedDecimalScale
															thousandSeparator=","
															size="small"
															style={{
																padding: '5px 11px',
																textAlign: 'right',
																height: '32px',
																borderRadius: '4px',
																fontSize: '13px',
																fontWeight: 'bold',
																backgroundColor: undefined,
																borderColor: undefined,
															}}
															customInput={Input}
															disabled={true}
															readOnly={true}
														/>
													</TableCell>
												</TableRow>
											</TableFooter>
										</Table>
									</TableContainer>

									<Form.Item name="advanceAmounts" noStyle />
								</>
							);
						}}
					</Form.List>
				</Col>

				<Col xs={24}>
					<Typography
						variant="h4"
						sx={{
							fontWeight: 600,
							fontSize: '18px',
							marginTop: '16px',
							color: '#1a3b5d',
							marginBottom: '16px',
							padding: '0 0 12px 0',
							borderBottom: '1px solid #e0e6ed',
						}}
					>
						Other Payments & Incentive
					</Typography>

					<Row gutter={[16, 0]}>
						<Col xs={24} md={6}>
							<InputLabel label={constantFormValues?.upfrontLeasePayment?.label} head={true} />
						</Col>
						<Col xs={24} md={9}>
							<Form.Item name={constantFormValues?.upfrontLeasePayment?.key} rules={constantFormValues?.upfrontLeasePayment?.rules}>
								<NumericFormat
									disabled
									size="large"
									decimalScale={2}
									placeholder="0.00"
									fixedDecimalScale
									customInput={Input}
									thousandSeparator=","
									isAllowed={withValueCap}
									style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={9}>
							<Form.Item
								name={`${constantFormValues?.upfrontLeasePayment?.key}_new`}
								rules={constantFormValues?.upfrontLeasePayment?.rules}
							>
								<NumericFormat
									disabled
									size="large"
									decimalScale={2}
									placeholder="0.00"
									fixedDecimalScale
									customInput={Input}
									thousandSeparator=","
									isAllowed={withValueCap}
									style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
								/>
							</Form.Item>
						</Col>

						<Col xs={24} md={6}>
							<InputLabel label={constantFormValues?.initialDirectCost?.label} head={true} />
						</Col>
						<Col xs={24} md={9}>
							<Form.Item name={constantFormValues?.initialDirectCost?.key} rules={constantFormValues?.initialDirectCost?.rules}>
								<NumericFormat
									disabled
									size="large"
									decimalScale={2}
									placeholder="0.00"
									fixedDecimalScale
									customInput={Input}
									thousandSeparator=","
									isAllowed={withValueCap}
									style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={9}>
							<Form.Item
								rules={constantFormValues?.initialDirectCost?.rules}
								name={`${constantFormValues?.initialDirectCost?.key}_new`}
							>
								<NumericFormat
									disabled
									size="large"
									decimalScale={2}
									placeholder="0.00"
									fixedDecimalScale
									customInput={Input}
									thousandSeparator=","
									isAllowed={withValueCap}
									style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
								/>
							</Form.Item>
						</Col>

						<Col xs={24} md={6}>
							<InputLabel label={constantFormValues?.securityDeposit?.label} head={true} />
						</Col>
						<Col xs={24} md={9}>
							<Form.Item name={constantFormValues?.securityDeposit?.key} rules={constantFormValues?.securityDeposit?.rules}>
								<NumericFormat
									disabled
									size="large"
									decimalScale={2}
									placeholder="0.00"
									fixedDecimalScale
									customInput={Input}
									thousandSeparator=","
									isAllowed={withValueCap}
									style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={9}>
							<Form.Item rules={constantFormValues?.securityDeposit?.rules} name={`${constantFormValues?.securityDeposit?.key}_new`}>
								<NumericFormat
									disabled
									size="large"
									decimalScale={2}
									placeholder="0.00"
									fixedDecimalScale
									customInput={Input}
									thousandSeparator=","
									isAllowed={withValueCap}
									style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
								/>
							</Form.Item>
						</Col>

						<Col xs={24} md={6}>
							<InputLabel label={constantFormValues?.cashIncentiveAmount?.label} head={true} />
						</Col>
						<Col xs={24} md={9}>
							<Form.Item name={constantFormValues?.cashIncentiveAmount?.key} rules={constantFormValues?.securityDeposit?.rules}>
								<NumericFormat
									disabled
									size="large"
									decimalScale={2}
									fixedDecimalScale
									placeholder="0.00"
									customInput={Input}
									thousandSeparator=","
									isAllowed={withValueCap}
									style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={9}>
							<Form.Item
								rules={constantFormValues?.securityDeposit?.rules}
								name={`${constantFormValues?.cashIncentiveAmount?.key}_new`}
							>
								<NumericFormat
									disabled
									size="large"
									decimalScale={2}
									fixedDecimalScale
									placeholder="0.00"
									customInput={Input}
									thousandSeparator=","
									isAllowed={withValueCap}
									style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
								/>
							</Form.Item>
						</Col>
					</Row>
				</Col>

				<Form.Item name="grossRents" noStyle />
				<Form.Item name="grossRents_new" noStyle />
				<Form.Item name="advanceAmounts" noStyle />
				<Form.Item name="advanceAmounts_new" noStyle />
				<Form.Item name={constantFormValues?.advanceAmount?.key} noStyle />
				<Form.Item name={`${constantFormValues?.advanceAmount?.key}_new`} noStyle />
				<Form.Item noStyle name={constantFormValues?.inputChangeMonth?.key} />
			</Row>

			<Typography
				variant="h4"
				sx={{
					fontWeight: 600,
					fontSize: '18px',
					color: '#1a3b5d',
					marginBottom: '16px',
					padding: '0 0 12px 0',
					borderBottom: '1px solid #e0e6ed',
				}}
			>
				IFRS - 16 Specific Fields
			</Typography>

			<Row gutter={[16, 16]}>
				<Col xs={24}>
					<Form.List name="changes">
						{(fields) => {
							return (
								<TableContainer
									sx={{
										width: '100%',
										display: 'block',
										overflowX: 'auto',
										position: 'relative',

										'& table, & th, & td': {
											border: '1px solid #e0e0e0',
											borderCollapse: 'collapse',
										},
										'& th, & td': {
											p: '5px',
										},
										'& td .ant-form-item': {
											marginBottom: '0px',
										},

										'& td:not(:last-child)': { borderRight: '1px solid #e0e0e0' },
										'& tr:not(:last-child)': { borderBottom: '1px solid #e0e0e0' },
										'& th': { borderBottom: '1px solid #e0e0e0' },
									}}
								>
									<Table
										sx={{
											'& .MuiTableBody-root .MuiTableCell-root': { px: '5px', py: '5px' },
											tableLayout: 'fixed',
										}}
									>
										<TableHead>
											<TableRow>
												<Form.Item noStyle shouldUpdate>
													{({ getFieldValue }) => {
														const leaseType = getFieldValue('leaseType');

														return (
															<>
																{constantFormValues?.changes?.orderedRowHeader.map((key) => {
																	if (key === 'inputChangeDiscountRate' && leaseType === 'Operating Lease')
																		return null;

																	return (
																		<TableCell
																			key={constantFormValues?.changes?.changeList[key].key}
																			sx={{ fontSize: '13px', backgroundColor: '#f6f5f5' }}
																			align="center"
																		>
																			{constantFormValues?.changes?.changeList[key].label}
																		</TableCell>
																	);
																})}
															</>
														);
													}}
												</Form.Item>
											</TableRow>
										</TableHead>

										<TableBody>
											{fields?.map((field, index) => {
												const maxDate = getMaxDateForRow(index);

												return (
													<TableRow key={index}>
														<TableCell style={{ position: 'relative' }}>
															<Form.Item
																style={{ margin: 0 }}
																name={[
																	field.name,
																	constantFormValues?.changes?.changeList['inputChangeEffectiveDate'].key,
																]}
																rules={constantFormValues?.changes?.changeList['inputChangeEffectiveDate'].rules}
															>
																<DatePicker
																	disabled={index === 0 ? true : initialChangeLength - 1 < index ? false : true}
																	onChange={(date, dateString) => {
																		if (date === null || date === undefined || dateString === '') {
																			handleInputMonthChange(null, index);
																		} else {
																			handleInputMonthChange(date, index);
																		}
																	}}
																	format="YYYY-MM-DD"
																	style={{ width: '100%', height: '32px' }}
																	className="antd-datePicker"
																	disabledDate={(current) => {
																		if (!current) return false;

																		const leaseStartDateValue = form.getFieldValue('leaseStartDate');
																		if (!leaseStartDateValue) return false;

																		const leaseStartDate = dayjs(leaseStartDateValue);
																		if (!leaseStartDate.isValid()) return false;

																		let minimumAllowedDate = leaseStartDate;

																		const changes = form.getFieldValue('changes') || [];
																		let lastEffectiveDate = null;

																		if (index > 0 && changes[index - 1]) {
																			lastEffectiveDate =
																				changes[index - 1]?.[
																					constantFormValues?.changes?.changeList?.inputChangeEffectiveDate
																						?.key
																				];
																		}

																		if (lastEffectiveDate) {
																			minimumAllowedDate = dayjs(lastEffectiveDate)
																				.add(1, 'month')
																				.startOf('month');
																		} else if (maxRecordedDate?.minimumDateLimit) {
																			minimumAllowedDate = dayjs(maxRecordedDate.minimumDateLimit)
																				.add(1, 'month')
																				.startOf('month');
																		}

																		const leaseEndDate = form.getFieldValue('leaseEndDate');
																		let isAfterMaximum = false;
																		if (leaseEndDate) {
																			isAfterMaximum = current.isAfter(dayjs(leaseEndDate), 'day');
																		} else if (maxDate) {
																			isAfterMaximum = current.isAfter(dayjs(maxDate), 'day');
																		}

																		const requiredDayOfMonth = leaseStartDate.date();
																		const isWrongDayOfMonth = current.date() !== requiredDayOfMonth;

																		const isBeforeMinimum = current.isBefore(minimumAllowedDate, 'day');

																		return isBeforeMinimum || isAfterMaximum || isWrongDayOfMonth;
																	}}
																/>
															</Form.Item>
														</TableCell>

														<TableCell>
															<Form.Item
																style={{ margin: 0 }}
																name={[
																	field.name,
																	constantFormValues?.changes?.changeList['inputChangeDescription'].key,
																]}
															>
																<Select
																	disabled
																	showSearch
																	style={{ textAlign: 'center' }}
																	filterOption={true}
																	options={
																		constantFormValues?.changes?.changeList['inputChangeDescription'].options
																	}
																/>
															</Form.Item>
														</TableCell>
														<TableCell>
															<Form.Item
																style={{ margin: 0 }}
																name={[
																	field.name,
																	constantFormValues?.changes?.changeList['inputChangeLeaseTerm'].key,
																]}
															>
																<Input
																	disabled
																	type="number"
																	style={{
																		marginBottom: 0,
																		textAlign: 'center',
																		color:
																			index !== 0
																				? form.getFieldValue(['changes', index, 'inputChangeLeaseTerm']) ===
																					form.getFieldValue(['changes', index - 1, 'inputChangeLeaseTerm'])
																					? 'black'
																					: 'blue'
																				: 'black',
																	}}
																/>
															</Form.Item>
														</TableCell>
														<Form.Item name={[field.name, 'inputChangeDiscountRate']} noStyle />

														<Form.Item noStyle shouldUpdate={(prev, current) => prev.leaseType !== current.leaseType}>
															{({ getFieldValue }) =>
																getFieldValue('leaseType') === 'Finance Lease' ? (
																	<TableCell align="center">
																		<Form.Item
																			style={{ margin: 0 }}
																			name={[
																				field.name,
																				constantFormValues?.changes?.changeList['inputChangeDiscountRate']
																					.key,
																			]}
																		>
																			<NumericFormat
																				size="large"
																				decimalScale={2}
																				placeholder="0.00"
																				fixedDecimalScale
																				thousandSeparator=","
																				isAllowed={withValueCap}
																				style={{
																					textAlign: 'center',
																					height: '32px',
																					borderRadius: '4px',
																					fontSize: '13px',
																					color:
																						index !== 0
																							? form.getFieldValue([
																									'changes',
																									index,
																									'inputChangeDiscountRate',
																								]) ===
																								form.getFieldValue([
																									'changes',
																									index - 1,
																									'inputChangeDiscountRate',
																								])
																								? 'black'
																								: 'blue'
																							: 'black',
																				}}
																				customInput={Input}
																				disabled
																			/>
																		</Form.Item>
																	</TableCell>
																) : null
															}
														</Form.Item>

														<TableCell>
															<Form.Item
																style={{ margin: 0 }}
																name={[field.name, constantFormValues?.changes?.changeList['paymentFrequency'].key]}
															>
																<Select
																	disabled
																	showSearch
																	filterOption={true}
																	style={{ textAlign: 'center' }}
																	options={constantFormValues?.changes?.changeList['paymentFrequency'].options}
																/>
															</Form.Item>
														</TableCell>

														<TableCell>
															<Form.Item
																style={{ margin: 0 }}
																name={[field.name, constantFormValues?.changes?.changeList['paymentTime'].key]}
															>
																<Select
																	disabled
																	showSearch
																	filterOption={true}
																	style={{ textAlign: 'center' }}
																	options={constantFormValues?.changes?.changeList['paymentTime'].options}
																/>
															</Form.Item>
														</TableCell>
													</TableRow>
												);
											})}
										</TableBody>
									</Table>
								</TableContainer>
							);
						}}
					</Form.List>
				</Col>
			</Row>

			<Box
				sx={{
					gap: '4px',
					mt: '16px',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'space-between',
				}}
			>
				<Spin spinning={isLeaseControlDataLoading}>
					<Box
						sx={{
							pt: 0,
							gap: '4px',
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'space-between',
						}}
					>
						<Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
							<Button
								type="primary"
								onClick={handleSubmit}
								style={{
									marginTop: 0,
									borderRadius: '4px',
									backgroundColor: theme.palette.lynTheme.main,
								}}
							>
								Update
							</Button>
						</Box>
					</Box>
				</Spin>

				{showClosedModal && (
					<ComptrollerConfirmModal
						modalData={modalData}
						showModal={showClosedModal}
						setShowModal={setShowClosedModal}
						handleSubmit={modalSubmitHandler}
					/>
				)}

				{formState?.showFormSubmitModal && (
					<ComptrollerConfirmModal
						modalData={formState?.modalData}
						handleSubmit={handleModalSubmit}
						setShowModal={setShowFormSubmitModal}
						showModal={formState?.showFormSubmitModal}
					/>
				)}
			</Box>
		</>
	);
};

export default LeaseRecordManagementV9ModifyBasicInfo;

// import dayjs from 'dayjs';
// import { toastConfig } from 'config';
// import { toast } from 'react-toastify';
// import { useSelector } from 'react-redux';
// import MainCard from 'components/MainCard';
// import { useEffect, useRef, useState } from 'react';
// import { withValueCap } from 'pages/globals/global';
// import { NumericFormat } from 'react-number-format';
// import InputLabel from 'components/InputLabel/index';
// import constantFormValues from '../utils/formInputNames';
// import { useLocation, useNavigate } from 'react-router-dom';
// import CardAccordion from 'components/CardAccordion/CardAccordion';
// import { useGetQueryV3Mutation } from 'store/features/api/graphSlice';
// import { useSendControlDataMutation } from 'store/features/api/apiSlice';
// import { MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
// import { useIdentityBulkMutation } from 'store/features/process/processApi';
// import { utcConverter, processArrays, stateUpdater } from '../utils/helperFunc';
// import ComptrollerConfirmModal from 'pages/comptroller/ComptrollerConfirmModal';
// import { Col, DatePicker, Form, Input, Row, Select, message, Button, Spin, Tooltip } from 'antd';
// import { Box, Table, useTheme, TableRow, TableHead, TableBody, TableCell, TableFooter, TableContainer } from '@mui/material';

// const LeaseRecordManagementV9ModifyBasicInfo = ({
// 	screen,
// 	vendorList,
// 	handleSubmit,
// 	viewData = {},
// 	dataFullyLoaded,
// 	initialChangeLength = 0,
// 	debouncedManageChangeRows,
// }) => {
// 	const form = Form.useFormInstance();
// 	const LeaseEndDate = dayjs().add(1, 'month');

// 	const currentLeaseTerm = Form.useWatch(constantFormValues?.leaseTerm?.key, form);
// 	const currentPaymentFrequency = Form.useWatch(constantFormValues?.paymentFrequency?.key, form);

// 	useEffect(() => {
// 		const currentPaymentFrequency = form.getFieldValue(constantFormValues?.paymentFrequency?.key);

// 		if (currentPaymentFrequency === 'Quarterly') {
// 			form.setFieldValue('modifiedLeaseTerm', Math.floor(form.getFieldValue('leaseTerm') / 3));
// 		} else {
// 			form.setFieldValue('modifiedLeaseTerm', form.getFieldValue('leaseTerm'));
// 		}
// 	}, [currentLeaseTerm, currentPaymentFrequency]);

// 	const grossRentKey = useRef('');
// 	const isUpdatingRef = useRef(false);
// 	const advanceAmountKey = useRef('');

// 	const grossRentList = Form.useWatch('grossRentList_new', form);
// 	const advanceAmountList = Form.useWatch('advanceAmountList_new', form);

// 	useEffect(() => {
// 		if (!Array.isArray(grossRentList) || !Array.isArray(advanceAmountList) || grossRentList.length === 0 || advanceAmountList.length === 0)
// 			return;

// 		const newGrossRentKey = grossRentList.map((item) => `${item.from}-${item.to}`).join('|');
// 		const newAdvanceAmountKey = advanceAmountList.map((item) => `${item.from}-${item.to}`).join('|');

// 		if (newGrossRentKey === grossRentKey.current && newAdvanceAmountKey === advanceAmountKey.current) return;

// 		if (isUpdatingRef.current) return;

// 		isUpdatingRef.current = true;

// 		try {
// 			grossRentKey.current = newGrossRentKey;
// 			advanceAmountKey.current = newAdvanceAmountKey;
// 			const maxGrossRentTerm = Math.max(...grossRentList.map((item) => item.to ?? 0));
// 			const maxAdvanceAmountTerm = Math.max(...advanceAmountList.map((item) => item.to ?? 0));
// 			const maxTerm = Math.max(maxGrossRentTerm, maxAdvanceAmountTerm);
// 			const currentLeaseTerm = form.getFieldValue('leaseTerm_new');

// 			if (currentLeaseTerm !== maxTerm) {
// 				form.setFieldValue('leaseTerm_new', maxTerm);
// 			}

// 			const leaseStartDate = form.getFieldValue('leaseStartDate');

// 			if (leaseStartDate && maxTerm) {
// 				const leaseEndDate = leaseStartDate.add(maxTerm, 'month').subtract(1, 'day');
// 				const currentLeaseEndDate = form.getFieldValue('leaseEndDate_new');

// 				if (!currentLeaseEndDate || !leaseEndDate.isSame(currentLeaseEndDate)) {
// 					form.setFieldValue('leaseEndDate_new', leaseEndDate);
// 				}
// 			}
// 		} finally {
// 			setTimeout(() => {
// 				isUpdatingRef.current = false;
// 			}, 0);
// 		}
// 	}, [grossRentList, advanceAmountList, screen]);

// 	const handlePaymentFrequencyChange = (value) => {
// 		if (isUpdatingRef.current) return value;

// 		isUpdatingRef.current = true;

// 		try {
// 			// callDebounced();
// 		} finally {
// 			isUpdatingRef.current = false;
// 		}

// 		return value;
// 	};

// 	const handlePaymentFrequencyBlur = () => {
// 		debouncedManageChangeRows();
// 	};

// 	const handlePaymentModalityChange = (value) => {
// 		if (isUpdatingRef.current) return value;

// 		isUpdatingRef.current = true;

// 		try {
// 		} finally {
// 			isUpdatingRef.current = false;
// 		}

// 		return value;
// 	};

// 	const handlePaymentModalityBlur = () => {
// 		debouncedManageChangeRows();
// 	};

// 	const callDebounced = () => {
// 		debouncedManageChangeRows();
// 	};

// 	const handleAmountChange = () => {
// 		const timeoutId = setTimeout(() => {
// 			handleApply();
// 		}, 10);

// 		return () => {
// 			clearTimeout(timeoutId);
// 		};
// 	};

// 	const handleToChange = () => {
// 		setTimeout(handleApply, 10);
// 	};

// 	const validateToValueOnBlur = (index) => {
// 		const currentValues = form.getFieldValue('grossRentList_new');
// 		if (!currentValues) return;

// 		const toValue = parseInt(currentValues[index]?.to);
// 		const fromValue = parseInt(currentValues[index]?.from);

// 		if (!isNaN(toValue) && !isNaN(fromValue) && toValue < fromValue) {
// 			message.error("'To' value must be greater than or equal to 'From' value");
// 			form.setFields([
// 				{
// 					name: ['grossRentList_new', index, 'to'],
// 					value: '',
// 					errors: ["'To' value must be greater than 'From' value"],
// 				},
// 			]);
// 		}

// 		setTimeout(handleApply, 10);
// 	};

// 	const isRowValid = (row) => {
// 		if (!row) return false;

// 		const toValue = parseInt(row.to);
// 		const fromValue = parseInt(row.from);

// 		if (isNaN(toValue) || toValue <= 0) return false;
// 		if (isNaN(fromValue) || fromValue <= 0) return false;
// 		if (toValue < fromValue) return false;
// 		if (!row.amount) return false;

// 		return true;
// 	};

// 	const handleAddRow = (index) => {
// 		const leaseType = form.getFieldValue('leaseType');
// 		const currentValues = form.getFieldValue('grossRentList_new');

// 		if (!currentValues || !currentValues.length) {
// 			form.setFieldValue('grossRentList_new', [{ from: 1, to: '', amount: '', remarks: '' }]);
// 			return;
// 		}

// 		const currentRow = currentValues[index];

// 		if (!isRowValid(currentRow)) {
// 			message.error('Please complete all required fields in the current row before adding a new one');
// 			return;
// 		}

// 		const toValue = parseInt(currentRow.to);

// 		if (leaseType === 'Operating Lease' && toValue >= 12) {
// 			message.error('Maximum periods taken');
// 			return;
// 		}
// 		const newFrom = toValue + 1;

// 		const newRow = { from: newFrom, to: '', amount: '', remarks: '' };

// 		const updatedValues = [...currentValues.slice(0, index + 1), newRow, ...currentValues.slice(index + 1)];

// 		for (let i = index + 2; i < updatedValues.length; i++) {
// 			const prevTo = parseInt(updatedValues[i - 1].to);
// 			if (!isNaN(prevTo)) {
// 				updatedValues[i].from = prevTo + 1;
// 			}
// 		}

// 		form.setFieldValue('grossRentList_new', updatedValues);

// 		setTimeout(handleApply, 10);
// 	};

// 	const handleApply = () => {
// 		const currentValues = form.getFieldValue('grossRentList_new');

// 		if (!currentValues || currentValues.length === 0) return;
// 		const transformedData = transformRangeDataToAmountArray(currentValues);

// 		form.setFieldValue('grossRents_new', transformedData);
// 		const currentAdvanceAmountsValues = form.getFieldValue('advanceAmountList_new');
// 		if (!currentAdvanceAmountsValues || currentAdvanceAmountsValues?.length === 0) return;

// 		const transformedDataAdvanceAmounts = transformRangeDataToAmountArray(currentAdvanceAmountsValues);

// 		const { processedAdvanceAmounts } = processArrays(transformedData, transformedDataAdvanceAmounts);

// 		form.setFieldValue('advanceAmounts_new', processedAdvanceAmounts);
// 		form.setFieldValue('leaseTerm_new', transformedData?.length);

// 		const leaseStartDate = form.getFieldValue('leaseStartDate');

// 		const leaseEndDate = leaseStartDate.add(transformedData?.length, 'month').subtract(1, 'day');

// 		form.setFieldValue('leaseEndDate_new', leaseEndDate);

// 		callDebounced();
// 	};

// 	useEffect(() => {
// 		const currentValues = form.getFieldValue('grossRentList_new');
// 		if (!currentValues || !currentValues.length) {
// 			form.setFieldValue('grossRentList_new', [{ from: 1, to: '', amount: '', remarks: '' }]);
// 		}

// 		// handleApply();
// 	}, [form]);

// 	//gross rent end

// 	// irfs 16

// 	const theme = useTheme();

// 	// action button

// 	const { state } = useLocation();
// 	const [formState, setFormState] = useState({
// 		showActiveDeactiveModal: false,
// 		showFormSubmitModal: false,
// 		submitPayload: null,
// 		modalData: {
// 			head: state?.processName ?? '',
// 			label: 'Do you want to proceed?',
// 		},
// 	});
// 	const updateFormState = stateUpdater(setFormState);
// 	const [draftSubmit, { isSuccess: isDraftSubmitSuccess }] = useIdentityBulkMutation();
// 	const handleSubmitClick = () => {
// 		const payloadData = [viewData?._id];
// 		const formattedPayload = {
// 			_id: payloadData,
// 			operation: 'submit-draft-bulk',
// 			processCode: state?.processCode,
// 			suffixCode: '',
// 		};

// 		updateFormState('showFormSubmitModal', true);
// 		updateFormState('modalData.head', 'Confirmation');
// 		updateFormState('modalData.label', 'Do you want to submit this lease record?');

// 		updateFormState('submitPayload', formattedPayload);
// 	};
// 	const handleModalSubmit = async () => {
// 		await draftSubmit(formState?.submitPayload);
// 	};
// 	const setShowFormSubmitModal = (value) => {
// 		updateFormState('showFormSubmitModal', value);
// 	};
// 	const [
// 		getLeaseTableData,
// 		{
// 			data: leaseControlDataV3,
// 			// isLoading: isLeaseTableDataLoading,
// 		},
// 	] = useGetQueryV3Mutation();
// 	const [IdentityBulk, { isSuccess: isCaseSuccess, isError: isCaseError }] = useIdentityBulkMutation();

// 	const [isDraft, setIsDraft] = useState(false);

// 	const navigate = useNavigate();

// 	const { creatorOrganizationId } = useSelector((state) => state.profile.profileData);
// 	const companyCode = creatorOrganizationId;
// 	const [getLeaseData, { isLoading: isLeaseControlDataLoading }] = useSendControlDataMutation();

// 	const isLeaseLifeCycleOver = () => {
// 		const currentDate = dayjs();
// 		const leaseEndDate = dayjs(form.getFieldValue('leaseEndDate'));

// 		return currentDate.isAfter(leaseEndDate);
// 	};

// 	const [areAllRecordsRecorded, setAreAllRecordsRecorded] = useState(false);
// 	const [showClosedModal, setShowClosedModal] = useState(false);
// 	const [modalData, setModalData] = useState({
// 		head: state?.processName ?? '',
// 		label: 'Do you want to proceed?',
// 	});
// 	const [activeDeactiveSubmitData, setActiveDeactiveSubmitData] = useState(null);

// 	const handleSingleLeaseDeactivate = (status) => {
// 		if (status === 'Closed') {
// 			setModalData({
// 				head: state?.processName ?? 'Lease Record',
// 				action: status,
// 				label: <p>Do you want to close this lease record?</p>,
// 			});
// 		}
// 		if (status === 'Reopened') {
// 			setModalData({
// 				head: state?.processName ?? 'Lease Record',
// 				action: status,
// 				label: <p>Do you want to re-open this lease record?</p>,
// 			});
// 		}

// 		const singleLeasePayload = [{ _id: state._id }];
// 		setActiveDeactiveSubmitData(singleLeasePayload);
// 		setShowClosedModal(true);
// 	};

// 	const modalSubmitHandler = () => {
// 		if (modalData?.action === 'Closed') {
// 			const payload = {
// 				_id: activeDeactiveSubmitData.map((item) => item._id),
// 				operation: 'activate-bulk',
// 				processCode: state?.processCode,
// 				activationStatus: 'Closed',
// 			};
// 			IdentityBulk(payload);
// 		} else if (modalData?.action === 'Reopened') {
// 			const payload = {
// 				_id: activeDeactiveSubmitData.map((item) => item._id),
// 				operation: 'activate-bulk',
// 				processCode: state?.processCode,
// 				activationStatus: 'Reopened',
// 			};
// 			IdentityBulk(payload);
// 		}
// 		setShowClosedModal(false);
// 	};

// 	useEffect(() => {
// 		if (isCaseSuccess) {
// 			toast.success('Success! Lease status updated successfully', toastConfig);
// 			navigate(-1);
// 		} else if (isCaseError) {
// 			toast.error('Failed! Status update not completed', toastConfig);
// 			setShowClosedModal(false);
// 		}
// 	}, [isCaseSuccess, isCaseError]);

// 	const queryV3Loader = async () => {
// 		if (screen !== 'CREATE') {
// 			const version = String(viewData?.data?.version);

// 			if (version && version !== 'null' && version !== 'undefined' && companyCode) {
// 				await getLeaseTableData({
// 					companyCode,
// 					fieldCodes: ['companyCode', 'identityRecordIdString', 'version'],
// 					fieldValues: [companyCode, state?._id, version],
// 					name: 'leaseSchedulesOnVersion-v9',
// 				});
// 			}
// 		}
// 	};
// 	useEffect(() => {
// 		if (isDraftSubmitSuccess) {
// 			navigate(-1);
// 		}
// 	}, [isDraftSubmitSuccess]);

// 	useEffect(() => {
// 		queryV3Loader();
// 		setIsDraft(viewData?.activationStatus?.toLowerCase() === 'draft');
// 	}, [dataFullyLoaded, viewData]);

// 	useEffect(() => {
// 		if (Array.isArray(leaseControlDataV3?.results)) {
// 			const isAllRecordsRecorded = leaseControlDataV3?.results?.every((item) => item?.processStatus === 'Recorded');

// 			setAreAllRecordsRecorded(isAllRecordsRecorded);
// 		}
// 	}, [JSON.stringify(leaseControlDataV3?.results)]);

// 	// advance amounts

// 	// State to track the total amount
// 	const [rowTotals, setRowTotals] = useState([]);
// 	const [totalAmount, setTotalAmount] = useState(0);

// 	const callDebouncedAdvanceAmount = () => {
// 		debouncedManageChangeRows();
// 	};

// 	const formAdvanceAmount = Form.useWatch(constantFormValues?.advanceAmount?.key, form);

// 	useEffect(() => {
// 		if (formAdvanceAmount !== undefined && formAdvanceAmount !== null) {
// 			// If the form value exists but differs from our state, update our state
// 			if (formAdvanceAmount !== totalAmount) {
// 				setTotalAmount(formAdvanceAmount);
// 			}
// 		}
// 	}, [formAdvanceAmount]);

// 	// Add this useEffect to prevent the form from overriding our calculated total
// 	useEffect(() => {
// 		// When totalAmount changes, make sure the form value is updated
// 		if (totalAmount > 0) {
// 			const currentFormValue = form.getFieldValue(constantFormValues?.advanceAmount?.key);

// 			// If the form value differs from our calculated total, update it
// 			if (currentFormValue !== totalAmount) {
// 				form.setFieldValue(constantFormValues?.advanceAmount?.key, totalAmount);
// 			}
// 		}
// 	}, [totalAmount]);

// 	function transformRangeDataToAmountArray(rangeData) {
// 		// Initialize an empty result array
// 		const result = [];

// 		// Process each range object
// 		rangeData.forEach((range) => {
// 			const fromValue = parseInt(range.from);
// 			const toValue = parseInt(range.to);

// 			// Skip invalid ranges
// 			if (isNaN(fromValue) || isNaN(toValue) || fromValue > toValue) {
// 				console.warn('Invalid range found:', range);
// 				return;
// 			}

// 			// Convert amount string to number (remove commas)
// 			const amount = parseFloat(String(range.amount).replace(/,/g, ''));

// 			// Skip if amount is not a valid number
// 			if (isNaN(amount)) {
// 				console.warn('Invalid amount found:', range);
// 				return;
// 			}

// 			// Calculate how many times to repeat the amount ((to - from) + 1)
// 			const repetitions = toValue - fromValue + 1;

// 			// Add the amount to the result array the specified number of times
// 			for (let i = 0; i < repetitions; i++) {
// 				result.push(amount);
// 			}
// 		});

// 		return result;
// 	}

// 	// Calculate row totals based on current form values
// 	const calculateRowTotals = () => {
// 		// if (!form || !formListName) return;

// 		try {
// 			// Get the current values from the form
// 			const values = form.getFieldValue('advanceAmountList');

// 			if (values && Array.isArray(values)) {
// 				const newRowTotals = values.map((item) => {
// 					if (!item) return 0;

// 					const fromValue = parseInt(item.from);
// 					const toValue = parseInt(item.to);

// 					// If amount exists, convert it from string to number
// 					let amount = 0;
// 					if (item.amount) {
// 						const amountStr = String(item.amount).replace(/,/g, '');
// 						amount = parseFloat(amountStr);
// 					}

// 					// Skip invalid values
// 					if (isNaN(fromValue) || isNaN(toValue) || isNaN(amount) || toValue < fromValue) {
// 						return 0;
// 					}

// 					// Calculate total: (to - from + 1) * amount
// 					return (toValue - fromValue + 1) * amount;
// 				});

// 				setRowTotals(newRowTotals);
// 			}
// 		} catch (error) {
// 			console.error('Error calculating row totals:', error);
// 		}
// 	};

// 	const calculateTotal = () => {
// 		try {
// 			// Get the current values from the form
// 			const values = form.getFieldValue('advanceAmountList');

// 			if (values && Array.isArray(values)) {
// 				// Calculate row totals directly without waiting for state update
// 				const currentRowTotals = values.map((item) => {
// 					if (!item) return 0;

// 					const fromValue = parseInt(item.from);
// 					const toValue = parseInt(item.to);

// 					// If amount exists, convert it from string to number
// 					let amount = 0;
// 					if (item.amount !== undefined && item.amount !== null && item.amount !== '') {
// 						const amountStr = String(item.amount).replace(/,/g, '');
// 						amount = parseFloat(amountStr);
// 					}

// 					// Skip invalid values
// 					if (isNaN(fromValue) || isNaN(toValue) || isNaN(amount) || toValue < fromValue) {
// 						return 0;
// 					}

// 					// Calculate total: (to - from + 1) * amount
// 					return (toValue - fromValue + 1) * amount;
// 				});

// 				// Update row totals state
// 				setRowTotals(currentRowTotals);

// 				// Now calculate the grand total using the current row totals
// 				const sum = currentRowTotals.reduce((total, rowTotal) => total + (rowTotal || 0), 0);

// 				// Update total amount state
// 				setTotalAmount(sum);
// 			}
// 		} catch (error) {
// 			console.error('Error calculating total:', error);
// 		}
// 	};

// 	const handleAmountChangeAdvanceAmount = () => {
// 		setTimeout(() => {
// 			calculateTotal();
// 			handleApply();
// 		}, 10);
// 	};

// 	const handleToChangeAdvanceAmount = () => {
// 		setTimeout(() => {
// 			calculateRowTotals();
// 			handleApply();
// 		}, 10);
// 	};

// 	const validateToValueOnBlurAdvanceAmount = (index) => {
// 		const currentValues = form.getFieldValue('advanceAmountList');
// 		if (!currentValues) return;

// 		const toValue = parseInt(currentValues[index].to);
// 		const fromValue = parseInt(currentValues[index].from);

// 		// Validate that To is greater than From
// 		if (!isNaN(toValue) && !isNaN(fromValue) && toValue < fromValue) {
// 			message.error("'To' value must be greater than 'From' value");
// 			form.setFields([
// 				{
// 					name: ['advanceAmountList', index, 'to'],
// 					value: '',
// 					errors: ["'To' value must be greater than 'From' value"],
// 				},
// 			]);
// 		}
// 		// Recalculate row totals after validation
// 		calculateRowTotals();
// 		handleApply();
// 	};

// 	const isRowValidAdvanceAmount = (row) => {
// 		if (!row) return false;

// 		const toValue = parseInt(row.to);
// 		const fromValue = parseInt(row.from);

// 		if (isNaN(toValue) || toValue <= 0) return false;
// 		if (isNaN(fromValue) || fromValue <= 0) return false;
// 		if (toValue < fromValue) return false;
// 		if (!row.amount) return false;

// 		return true;
// 	};

// 	useEffect(() => {
// 		if (screen === 'VIEW' && dataFullyLoaded) {
// 			const timeoutId = setTimeout(() => {
// 				const currentValues = form.getFieldValue('advanceAmountList');

// 				if (currentValues && currentValues.length > 0) {
// 					calculateTotal();
// 				}
// 			}, 700);

// 			return () => clearTimeout(timeoutId);
// 		}
// 	}, [screen, dataFullyLoaded]);

// 	const handleApplyAdvanceAmount = () => {
// 		const currentValues = form.getFieldValue('advanceAmountList');
// 		if (!currentValues || currentValues?.length === 0) return;

// 		const transformedData = transformRangeDataToAmountArray(currentValues);
// 		const grossRents = form.getFieldValue('grossRents');
// 		const { processedAdvanceAmounts } = processArrays(grossRents, transformedData);

// 		const total = transformedData.reduce((sum, amount) => sum + amount, 0);

// 		// First set the advanceAmounts array for API submission
// 		form.setFieldValue('advanceAmounts', processedAdvanceAmounts);

// 		// Then update local state
// 		setTotalAmount(total);

// 		// Use a more direct approach to update the form and trigger change detection
// 		form.setFieldsValue({
// 			advanceAmount: total,
// 			advanceAmounts: processedAdvanceAmounts,
// 		});

// 		// Force the form to validate and notify watchers
// 		form.validateFields(['advanceAmounts', 'advanceAmount']);

// 		// Call debouncedManageChangeRows when data changes in Edit mode
// 		callDebouncedAdvanceAmount();
// 	};

// 	useEffect(() => {
// 		const currentValues = form.getFieldValue('advanceAmountList');
// 		if (screen === 'VIEW') {
// 			return;
// 		}
// 		if (!currentValues || !currentValues.length) {
// 			form.setFieldValue('advanceAmountList', [{ from: 1, to: '', amount: '', totalAmount: '' }]);
// 		}

// 		calculateTotal();
// 		calculateRowTotals();
// 	}, []);

// 	useEffect(() => {
// 		if (totalAmount > 0) {
// 			form.setFieldValue(constantFormValues?.advanceAmount?.key, totalAmount);

// 			const currentChanges = form.getFieldValue('changes');

// 			if (currentChanges && currentChanges.length > 0) {
// 				const updatedChanges = [...currentChanges];
// 				updatedChanges[0] = {
// 					...updatedChanges[0],
// 					inputChangeAdvance: totalAmount,
// 				};
// 			}
// 		}
// 	}, [totalAmount]);

// 	const { maxRecordedDate } = useSelector((state) => state.lease);

// 	const handleInputMonthChange = (e, index) => {
// 		if (!e) {
// 			const currentChanges = form.getFieldValue('changes') || [];
// 			if (currentChanges[index]) {
// 				const updatedChanges = [...currentChanges];
// 				updatedChanges[index] = {
// 					...updatedChanges[index],
// 					inputChangeMonth: null,
// 					inputChangeEffectiveDate: null,
// 				};
// 				form.setFieldsValue({ changes: updatedChanges });
// 			}
// 			return;
// 		}
// 		const effectiveDate = dayjs(e);
// 		const leaseStart = dayjs(form.getFieldValue('leaseStartDate'));
// 		let monthDifference = (effectiveDate.year() - leaseStart.year()) * 12 + (effectiveDate.month() - leaseStart.month());
// 		monthDifference += 1;
// 		if (monthDifference < 0) {
// 			monthDifference = 0;
// 		}
// 		const currentChanges = form.getFieldValue('changes') || [];
// 		if (currentChanges[index]) {
// 			const updatedChanges = [...currentChanges];
// 			updatedChanges[index] = {
// 				...updatedChanges[index],
// 				inputChangeMonth: monthDifference,
// 				inputChangeEffectiveDate: utcConverter(effectiveDate),
// 			};
// 			form.setFieldsValue({ changes: updatedChanges });
// 		}
// 	};

// 	const getMaxDateForRow = (index) => {
// 		if (index === 0) {
// 			return null;
// 		}
// 		const leaseStartDate = dayjs(form.getFieldValue('leaseStartDate'));
// 		if (!leaseStartDate) return null;
// 		const changes = form.getFieldValue('changes');
// 		if (!changes || !changes[index - 1]) return null;
// 		const prevRowLeaseTerm = changes[index - 1].inputChangeLeaseTerm;
// 		if (!prevRowLeaseTerm) return null;
// 		return leaseStartDate.add(parseInt(prevRowLeaseTerm), 'month').subtract(1, 'day');
// 	};

// 	return (
// 		<>
// 			<CardAccordion
// 				sx={{ mb: 2 }}
// 				darkTitle={true}
// 				title="Basic Information"
// 				typographySX={{ fontSize: '16px !important' }}
// 				contentSX={{ paddingBottom: '0 !important' }}
// 				customHeaderSX={{
// 					p: '10px',
// 					backgroundColor: '#e1f0fb !important',
// 				}}
// 			>
// 				<Row gutter={[16, 0]}>
// 					<Form.Item noStyle name="tabIndex" />
// 					<Form.Item noStyle name="endOfFinancialYear" />
// 					<Form.Item noStyle name={constantFormValues?.version?.key} />
// 					<Form.Item noStyle name={constantFormValues?.leaseTerm?.key} />
// 					<Form.Item noStyle name={constantFormValues?.lessorName?.key} />
// 					<Form.Item noStyle name={constantFormValues?.creationDate?.key} />
// 					<Form.Item noStyle name={constantFormValues?.paymentModality?.key} />
// 					<Form.Item noStyle name={constantFormValues?.lastModifiedDate?.key} />
// 					<Form.Item noStyle name={`${constantFormValues?.lessorName?.key}_new`} />
// 					<Form.Item noStyle name={constantFormValues?.paymentModalitylabel?.key} />

// 					<Col xs={24} md={6}>
// 						<InputLabel label={constantFormValues?.LeaseId?.label} head={true} />
// 					</Col>
// 					<Col xs={24} md={9}>
// 						<Form.Item name={constantFormValues?.LeaseId?.key}>
// 							<Input disabled placeholder="To be auto generated" style={{ height: '32px', borderRadius: '4px' }} />
// 						</Form.Item>
// 					</Col>
// 					<Col xs={24} md={9}>
// 						<Form.Item name={constantFormValues?.LeaseId?.key}>
// 							<Input disabled placeholder="To be auto generated" style={{ height: '32px', borderRadius: '4px' }} />
// 						</Form.Item>
// 					</Col>

// 					<Col xs={24} md={6}>
// 						<InputLabel label={constantFormValues?.leaseStartDate?.label} head={true} />
// 					</Col>
// 					<Col xs={24} md={9}>
// 						<Form.Item name={constantFormValues?.leaseStartDate?.key} rules={constantFormValues?.leaseStartDate?.rules}>
// 							<DatePicker disabled format="YYYY-MM-DD" style={{ width: '100%', height: '32px', borderRadius: '4px' }} />
// 						</Form.Item>
// 					</Col>
// 					<Col xs={24} md={9}>
// 						<Form.Item name={constantFormValues?.leaseStartDate?.key} rules={constantFormValues?.leaseStartDate?.rules}>
// 							<DatePicker disabled format="YYYY-MM-DD" style={{ width: '100%', height: '32px', borderRadius: '4px' }} />
// 						</Form.Item>
// 					</Col>

// 					<Col xs={24} md={6}>
// 						<InputLabel label={constantFormValues?.leaseTitle?.label} head={true} />
// 					</Col>
// 					<Col xs={24} md={9}>
// 						<Form.Item name={constantFormValues?.leaseTitle?.key} rules={constantFormValues?.leaseTitle?.rules}>
// 							<Input disabled placeholder="Write here" style={{ height: '32px', borderRadius: '4px' }} />
// 						</Form.Item>
// 					</Col>
// 					<Col xs={24} md={9}>
// 						<Form.Item name={`${constantFormValues?.leaseTitle?.key}_new`} rules={constantFormValues?.leaseTitle?.rules}>
// 							<Input placeholder="Write here" style={{ height: '32px', borderRadius: '4px' }} />
// 						</Form.Item>
// 					</Col>

// 					<Col xs={24} md={6}>
// 						<InputLabel label={constantFormValues?.lessorID?.label} head={true} />
// 					</Col>
// 					<Col xs={24} md={9}>
// 						<Form.Item name={constantFormValues?.lessorID?.key} rules={constantFormValues?.lessorID?.rules}>
// 							<Select
// 								showSearch
// 								disabled={true}
// 								filterOption={true}
// 								optionFilterProp="children"
// 								placeholder="Select from the list"
// 								style={{ width: '100%', height: '32px', borderRadius: '4px' }}
// 								onSelect={(_, selectItem) => form.setFieldValue(constantFormValues?.lessorName?.key, selectItem?.children)}
// 							>
// 								{vendorList?.map(({ label, value }) => (
// 									<Select.Option key={value} value={value}>
// 										{label}
// 									</Select.Option>
// 								))}
// 							</Select>
// 						</Form.Item>
// 					</Col>
// 					<Col xs={24} md={9}>
// 						<Form.Item name={constantFormValues?.lessorID?.key} rules={constantFormValues?.lessorID?.rules}>
// 							<Select
// 								showSearch
// 								disabled={true}
// 								filterOption={true}
// 								optionFilterProp="children"
// 								placeholder="Select from the list"
// 								style={{ width: '100%', height: '32px', borderRadius: '4px' }}
// 								onSelect={(_, selectItem) => form.setFieldValue(`${constantFormValues?.lessorName?.key}_new`, selectItem?.children)}
// 							>
// 								{vendorList?.map(({ label, value }) => (
// 									<Select.Option key={value} value={value}>
// 										{label}
// 									</Select.Option>
// 								))}
// 							</Select>
// 						</Form.Item>
// 					</Col>

// 					<Col xs={24} md={6}>
// 						<InputLabel label={constantFormValues?.leaseTerm?.label} head={true} />
// 					</Col>
// 					<Col xs={24} md={9}>
// 						<Form.Item name="modifiedLeaseTerm" rules={constantFormValues?.leaseTerm?.rules} noStyle>
// 							<Input disabled type="number" placeholder="Write here" style={{ height: '32px', borderRadius: '4px' }} />
// 						</Form.Item>
// 					</Col>
// 					<Col xs={24} md={9}>
// 						<Form.Item name="leaseTerm_new" rules={constantFormValues?.leaseTerm?.rules} noStyle>
// 							<Input type="number" disabled placeholder="Write here" style={{ height: '32px', borderRadius: '4px' }} />
// 						</Form.Item>
// 					</Col>

// 					<Col xs={24} md={6}>
// 						<InputLabel label={constantFormValues?.leaseEndDate?.label} head={true} />
// 					</Col>
// 					<Col xs={24} md={9}>
// 						<Form.Item name={constantFormValues?.leaseEndDate?.key} rules={constantFormValues?.leaseEndDate?.rules}>
// 							<DatePicker disabled value={LeaseEndDate} style={{ width: '100%', height: '32px', borderRadius: '4px' }} />
// 						</Form.Item>
// 					</Col>
// 					<Col xs={24} md={9}>
// 						<Form.Item name={`${constantFormValues?.leaseEndDate?.key}_new`} rules={constantFormValues?.leaseEndDate?.rules}>
// 							<DatePicker disabled value={LeaseEndDate} style={{ width: '100%', height: '32px', borderRadius: '4px' }} />
// 						</Form.Item>
// 					</Col>

// 					<Col xs={24} md={6}>
// 						<InputLabel label={constantFormValues?.leaseType?.label} head={true} />
// 					</Col>
// 					<Col xs={24} md={9}>
// 						<Form.Item name={constantFormValues?.leaseType?.key} rules={constantFormValues?.leaseType?.rules}>
// 							<Select
// 								disabled
// 								showSearch
// 								filterOption={true}
// 								optionFilterProp="children"
// 								placeholder="Select from the list"
// 								style={{ width: '100%', height: '32px', borderRadius: '4px' }}
// 								onSelect={(value) => {
// 									if (value === 'Operating Lease') {
// 										form.setFieldValue('grossRentList', [{ from: 1, to: '', amount: '', remarks: '' }]);
// 										form.setFieldValue('advanceAmountList', [
// 											{
// 												from: 1,
// 												to: '',
// 												amount: '',
// 												totalAmount: '',
// 											},
// 										]);
// 									}
// 								}}
// 							>
// 								{constantFormValues?.leaseType?.options?.map(({ key, value, label }) => (
// 									<Select.Option key={key} value={value}>
// 										{label}
// 									</Select.Option>
// 								))}
// 							</Select>
// 						</Form.Item>
// 					</Col>
// 					<Col xs={24} md={9}>
// 						<Form.Item name={`${constantFormValues?.leaseType?.key}`} rules={constantFormValues?.leaseType?.rules}>
// 							<Select
// 								disabled
// 								showSearch
// 								filterOption={true}
// 								optionFilterProp="children"
// 								placeholder="Select from the list"
// 								style={{ width: '100%', height: '32px', borderRadius: '4px' }}
// 								onSelect={(value) => {
// 									if (value === 'Operating Lease') {
// 										form.setFieldValue('grossRentList', [{ from: 1, to: '', amount: '', remarks: '' }]);
// 										form.setFieldValue('advanceAmountList', [
// 											{
// 												from: 1,
// 												to: '',
// 												amount: '',
// 												totalAmount: '',
// 											},
// 										]);
// 									}
// 								}}
// 							>
// 								{constantFormValues?.leaseType?.options?.map(({ key, value, label }) => (
// 									<Select.Option key={key} value={value}>
// 										{label}
// 									</Select.Option>
// 								))}
// 							</Select>
// 						</Form.Item>
// 					</Col>

// 					<Col xs={24}>
// 						<Form.Item
// 							shouldUpdate={(previousValue, currentValue) =>
// 								previousValue?.[constantFormValues?.leaseType?.key] !== currentValue?.[constantFormValues?.leaseType?.key]
// 							}
// 							noStyle
// 						>
// 							{({ getFieldValue }) => {
// 								if (getFieldValue(constantFormValues?.leaseType?.key) === 'Finance Lease') {
// 									return (
// 										<Row gutter={[16, 0]}>
// 											<Col xs={24} md={6}>
// 												<InputLabel label={constantFormValues?.initialDiscountRate?.label} head={true} />
// 											</Col>
// 											<Col xs={24} md={9}>
// 												<Form.Item
// 													name={constantFormValues?.initialDiscountRate?.key}
// 													rules={constantFormValues?.initialDiscountRate?.rules}
// 												>
// 													<Input disabled placeholder="Write here" style={{ height: '32px', borderRadius: '4px' }} />
// 												</Form.Item>
// 											</Col>
// 											<Col xs={24} md={9}>
// 												<Form.Item
// 													rules={constantFormValues?.initialDiscountRate?.rules}
// 													name={`${constantFormValues?.initialDiscountRate?.key}_new`}
// 												>
// 													<Input
// 														placeholder="Write here"
// 														style={{ height: '32px', borderRadius: '4px' }}
// 														onChange={(e) => {
// 															const value = e.target.value;
// 															// const currentChanges = form.getFieldValue('changes');

// 															form.setFieldValue(`${constantFormValues?.initialDiscountRate?.key}_new`, value);

// 															// form.setFieldValue(
// 															// 	['changes', currentChanges.length - 1, 'inputChangeDiscountRate'],
// 															// 	value
// 															// );

// 															callDebounced();
// 														}}
// 													/>
// 												</Form.Item>
// 											</Col>
// 										</Row>
// 									);
// 								} else {
// 									return <Form.Item name={constantFormValues?.initialDiscountRate?.key} noStyle />;
// 								}
// 							}}
// 						</Form.Item>
// 					</Col>
// 				</Row>
// 			</CardAccordion>

// 			<Row gutter={[16, 0]}>
// 				<Col xs={24} md={12}>
// 					<CardAccordion
// 						sx={{ mb: 2 }}
// 						darkTitle={false}
// 						title="Lease Payment Schedule"
// 						typographySX={{ fontSize: '16px !important' }}
// 						customHeaderSX={{ backgroundColor: '#e1f0fb !important', p: '10px' }}
// 					>
// 						<Form.Item name="changes" noStyle />

// 						<Form.Item name="identityId" noStyle />
// 						<Form.Item name="grossRentListSet" noStyle />
// 						<Form.Item name="effectiveDateHistory" noStyle />
// 						<Form.Item name="modificationSequence" noStyle />

// 						<>
// 							<Form.List name={'grossRentList'}>
// 								{(fields) => (
// 									<>
// 										<TableContainer
// 											className="gross-rent-table"
// 											sx={{
// 												'& table, & th, & td': {
// 													border: '1px solid #e0e0e0',
// 													borderCollapse: 'collapse',
// 												},
// 												'& th, & td': {
// 													p: '5px',
// 												},
// 												'& td .ant-form-item': {
// 													marginBottom: '0px',
// 												},

// 												'& th:nth-of-type(1), & td:nth-of-type(1)': { width: '30%' },
// 												'& th:nth-of-type(2), & td:nth-of-type(2)': { width: '30%' },
// 												'& th:nth-of-type(3), & td:nth-of-type(3)': { width: '30%' },

// 												'& table': { width: '100%' },
// 											}}
// 										>
// 											<Table aria-label="Gross Rent table">
// 												<TableHead>
// 													<TableRow>
// 														<TableCell sx={{ textAlign: 'center' }}>From Month</TableCell>
// 														<TableCell sx={{ textAlign: 'center' }}>To Month</TableCell>
// 														<TableCell sx={{ textAlign: 'center' }}>Amount</TableCell>
// 													</TableRow>
// 												</TableHead>

// 												<TableBody>
// 													{fields.length > 0 ? (
// 														fields.map((field, index) => (
// 															<TableRow key={field.key}>
// 																<TableCell>
// 																	<Form.Item name={[field.name, 'from']}>
// 																		<Input
// 																			disabled={true}
// 																			style={{
// 																				width: '100%',
// 																				padding: '8px',
// 																				textAlign: 'center',
// 																				backgroundColor: '#f5f5f5',
// 																			}}
// 																		/>
// 																	</Form.Item>
// 																</TableCell>

// 																<TableCell>
// 																	<Form.Item
// 																		name={[field.name, 'to']}
// 																		rules={[
// 																			{ required: true, message: "Please enter a 'To' value" },
// 																			({ getFieldValue }) => ({
// 																				validator(_, value) {
// 																					const leaseType = getFieldValue('leaseType');
// 																					const fromValue = getFieldValue([
// 																						'grossRentList',
// 																						field.name,
// 																						'from',
// 																					]);
// 																					if (!value || !fromValue) return Promise.resolve();
// 																					if (parseInt(value) < parseInt(fromValue)) {
// 																						return Promise.reject(
// 																							new Error("'To' must be greater than 'From'")
// 																						);
// 																					}
// 																					if (leaseType === 'Operating Lease' && parseInt(value) > 12) {
// 																						return Promise.reject(new Error('Maximum month is 12'));
// 																					}
// 																					return Promise.resolve();
// 																				},
// 																			}),
// 																		]}
// 																	>
// 																		<Input
// 																			disabled
// 																			type="number"
// 																			placeholder="Enter To value"
// 																			style={{
// 																				width: '100%',
// 																				padding: '8px',
// 																				textAlign: 'center',
// 																			}}
// 																		/>
// 																	</Form.Item>
// 																</TableCell>

// 																<TableCell>
// 																	<Form.Item
// 																		name={[field.name, 'amount']}
// 																		rules={[{ required: true, message: 'Please enter an amount' }]}
// 																	>
// 																		<NumericFormat
// 																			size="small"
// 																			disabled={true}
// 																			decimalScale={2}
// 																			placeholder="0.00"
// 																			fixedDecimalScale
// 																			thousandSeparator=","
// 																			customInput={Input}
// 																			isAllowed={withValueCap}
// 																			onBlur={() => setTimeout(handleApply, 10)}
// 																			style={{
// 																				height: '32px',
// 																				padding: '18px',
// 																				fontSize: '13px',
// 																				textAlign: 'right',
// 																				borderRadius: '4px',
// 																			}}
// 																			onValueChange={(values) => {
// 																				handleAmountChange();

// 																				const currentValues = [...form.getFieldValue('grossRentList')];

// 																				if (currentValues[index]) {
// 																					currentValues[index].amount = values.value || '';

// 																					form.setFieldValue('grossRentList', currentValues);
// 																				}
// 																			}}
// 																		/>
// 																	</Form.Item>
// 																</TableCell>
// 															</TableRow>
// 														))
// 													) : (
// 														<TableRow>
// 															<TableCell colSpan={5} style={{ textAlign: 'center' }}>
// 																No data. Click plus icon in action column to add new entries.
// 															</TableCell>
// 														</TableRow>
// 													)}
// 												</TableBody>
// 											</Table>
// 										</TableContainer>

// 										<Form.Item name="grossRents" noStyle />
// 									</>
// 								)}
// 							</Form.List>
// 						</>
// 					</CardAccordion>

// 					<Row gutter={[16, 0]}>
// 						<Col xs={24} md={6}>
// 							<InputLabel label={constantFormValues?.paymentFrequency?.label} head={true} />
// 						</Col>

// 						<Col xs={24} md={6}>
// 							<Form.Item name={constantFormValues?.paymentFrequency?.key} rules={constantFormValues?.paymentFrequency?.rules}>
// 								<Select
// 									disabled
// 									showSearch
// 									filterOption={true}
// 									optionFilterProp="children"
// 									placeholder="Select from the list"
// 									style={{ width: '100%', height: '32px', borderRadius: '4px' }}
// 								>
// 									{constantFormValues?.paymentFrequency?.options?.map(({ key, value, label }) => (
// 										<Select.Option key={key} value={value}>
// 											{label}
// 										</Select.Option>
// 									))}
// 								</Select>
// 							</Form.Item>
// 						</Col>

// 						<Col xs={24} md={6}>
// 							<InputLabel label={constantFormValues?.paymentModality?.label} head={true} />
// 						</Col>

// 						<Col xs={24} md={6}>
// 							<Form.Item name={constantFormValues?.paymentModality?.key} rules={constantFormValues?.paymentModality?.rules}>
// 								<Select
// 									disabled
// 									showSearch
// 									filterOption={true}
// 									optionFilterProp="children"
// 									placeholder="Select from the list"
// 									style={{ width: '100%', height: '32px', borderRadius: '4px' }}
// 								>
// 									{constantFormValues?.paymentModality?.options?.map(({ key, value, label }) => (
// 										<Select.Option key={key} value={value}>
// 											{label}
// 										</Select.Option>
// 									))}
// 								</Select>
// 							</Form.Item>
// 						</Col>
// 					</Row>
// 				</Col>

// 				<Col xs={24} md={12}>
// 					<CardAccordion
// 						sx={{ mb: 2 }}
// 						darkTitle={false}
// 						title="Lease Payment Schedule"
// 						typographySX={{ fontSize: '16px !important' }}
// 						customHeaderSX={{ backgroundColor: '#e1f0fb !important', p: '10px' }}
// 					>
// 						<Form.Item name="identityId" noStyle />
// 						<Form.Item name="grossRentListSet" noStyle />
// 						<Form.Item name="effectiveDateHistory" noStyle />
// 						<Form.Item name="modificationSequence" noStyle />

// 						<>
// 							<Form.List name={'grossRentList_new'}>
// 								{(fields) => (
// 									<>
// 										<TableContainer
// 											className="gross-rent-table"
// 											sx={{
// 												'& table, & th, & td': {
// 													border: '1px solid #e0e0e0',
// 													borderCollapse: 'collapse',
// 												},
// 												'& th, & td': {
// 													p: '5px',
// 												},
// 												'& td .ant-form-item': {
// 													marginBottom: '0px',
// 												},

// 												'& th:nth-of-type(1), & td:nth-of-type(1)': { width: '30%' },
// 												'& th:nth-of-type(2), & td:nth-of-type(2)': { width: '30%' },
// 												'& th:nth-of-type(3), & td:nth-of-type(3)': { width: '30%' },

// 												'& th:nth-of-type(4), & td:nth-of-type(4)': { width: '10%' },

// 												'& table': { width: '100%' },
// 											}}
// 										>
// 											<Table aria-label="Gross Rent table">
// 												<TableHead>
// 													<TableRow>
// 														<TableCell sx={{ textAlign: 'center' }}>From Month</TableCell>
// 														<TableCell sx={{ textAlign: 'center' }}>To Month</TableCell>
// 														<TableCell sx={{ textAlign: 'center' }}>Amount</TableCell>
// 														<TableCell sx={{ textAlign: 'center' }}>Action</TableCell>
// 													</TableRow>
// 												</TableHead>

// 												<TableBody>
// 													{fields.length > 0 ? (
// 														fields.map((field, index) => (
// 															<TableRow key={field.key}>
// 																<TableCell>
// 																	<Form.Item name={[field.name, 'from']}>
// 																		<Input
// 																			disabled={true}
// 																			style={{
// 																				width: '100%',
// 																				padding: '8px',
// 																				textAlign: 'center',
// 																				backgroundColor: '#f5f5f5',
// 																			}}
// 																		/>
// 																	</Form.Item>
// 																</TableCell>

// 																<TableCell>
// 																	<Form.Item
// 																		name={[field.name, 'to']}
// 																		rules={[
// 																			{ required: true, message: "Please enter a 'To' value" },
// 																			({ getFieldValue }) => ({
// 																				validator(_, value) {
// 																					const leaseType = getFieldValue('leaseType');
// 																					const fromValue = getFieldValue([
// 																						'grossRentList_new',
// 																						field.name,
// 																						'from',
// 																					]);
// 																					if (!value || !fromValue) return Promise.resolve();
// 																					if (parseInt(value) < parseInt(fromValue)) {
// 																						return Promise.reject(
// 																							new Error("'To' must be greater than 'From'")
// 																						);
// 																					}
// 																					if (leaseType === 'Operating Lease' && parseInt(value) > 12) {
// 																						return Promise.reject(new Error('Maximum month is 12'));
// 																					}
// 																					return Promise.resolve();
// 																				},
// 																			}),
// 																		]}
// 																	>
// 																		<Input
// 																			type="number"
// 																			placeholder="Enter To value"
// 																			onChange={() => handleToChange()}
// 																			onBlur={() => validateToValueOnBlur(index)}
// 																			style={{
// 																				width: '100%',
// 																				padding: '8px',

// 																				textAlign: 'center',
// 																			}}
// 																		/>
// 																	</Form.Item>
// 																</TableCell>
// 																<TableCell>
// 																	<Form.Item
// 																		name={[field.name, 'amount']}
// 																		rules={[{ required: true, message: 'Please enter an amount' }]}
// 																	>
// 																		<NumericFormat
// 																			size="small"
// 																			decimalScale={2}
// 																			placeholder="0.00"
// 																			fixedDecimalScale
// 																			thousandSeparator=","
// 																			isAllowed={withValueCap}
// 																			customInput={Input}
// 																			disabled={screen === 'VIEW'}
// 																			onBlur={() => setTimeout(handleApply, 10)}
// 																			style={{
// 																				height: '32px',
// 																				padding: '18px',
// 																				fontSize: '13px',
// 																				textAlign: 'right',
// 																				borderRadius: '4px',
// 																			}}
// 																			onValueChange={(values) => {
// 																				handleAmountChange(`${'grossRentList_new'}.${field.name}.amount`);
// 																				const currentValues = [...form.getFieldValue('grossRentList_new')];

// 																				if (currentValues[index]) {
// 																					currentValues[index].amount = values.value || '';

// 																					form.setFieldValue('grossRentList_new', currentValues);
// 																				}
// 																			}}
// 																		/>
// 																	</Form.Item>
// 																</TableCell>

// 																<TableCell align="center">
// 																	<>
// 																		{fields.length === 1 && index === 0 && (
// 																			<Tooltip title="Add Row">
// 																				<PlusCircleOutlined
// 																					style={{
// 																						fontSize: '20px',
// 																						color: '#1890ff',
// 																						cursor: 'pointer',
// 																					}}
// 																					onClick={() => handleAddRow(index)}
// 																				/>
// 																			</Tooltip>
// 																		)}

// 																		{fields.length > 1 && index > 0 && index < fields.length - 1 && (
// 																			<Tooltip title="Delete Row">
// 																				<MinusCircleOutlined
// 																					style={{
// 																						fontSize: '20px',
// 																						color: '#ff4d4f',
// 																						cursor: 'pointer',
// 																					}}
// 																					onClick={() => {
// 																						const currentValues = [
// 																							...form.getFieldValue('grossRentList_new'),
// 																						];

// 																						currentValues.splice(index, 1);

// 																						for (let i = index; i < currentValues.length; i++) {
// 																							if (i > 0) {
// 																								const prevTo = parseInt(currentValues[i - 1].to);
// 																								if (!isNaN(prevTo)) {
// 																									currentValues[i].from = prevTo + 1;
// 																								}
// 																							}
// 																						}

// 																						form.setFieldValue('grossRentList_new', currentValues);

// 																						setTimeout(handleApply, 10);
// 																					}}
// 																				/>
// 																			</Tooltip>
// 																		)}

// 																		{fields.length > 1 && index === fields.length - 1 && (
// 																			<>
// 																				<Tooltip title="Add Row">
// 																					<PlusCircleOutlined
// 																						style={{
// 																							fontSize: '20px',
// 																							color: '#1890ff',
// 																							marginRight: '12px',
// 																							cursor: 'pointer',
// 																						}}
// 																						onClick={() => handleAddRow(index)}
// 																					/>
// 																				</Tooltip>
// 																				<Tooltip title="Delete Row">
// 																					<MinusCircleOutlined
// 																						style={{
// 																							fontSize: '20px',
// 																							color: '#ff4d4f',
// 																							cursor: 'pointer',
// 																						}}
// 																						onClick={() => {
// 																							const currentValues = [
// 																								...form.getFieldValue('grossRentList_new'),
// 																							];

// 																							currentValues.splice(index, 1);

// 																							for (let i = index; i < currentValues.length; i++) {
// 																								if (i > 0) {
// 																									const prevTo = parseInt(currentValues[i - 1].to);
// 																									if (!isNaN(prevTo)) {
// 																										currentValues[i].from = prevTo + 1;
// 																									}
// 																								}
// 																							}

// 																							form.setFieldValue('grossRentList_new', currentValues);

// 																							setTimeout(handleApply, 10);
// 																						}}
// 																					/>
// 																				</Tooltip>
// 																			</>
// 																		)}

// 																		{fields.length > 1 && index === 0 && <span></span>}
// 																	</>
// 																</TableCell>
// 															</TableRow>
// 														))
// 													) : (
// 														<TableRow>
// 															<TableCell colSpan={5} style={{ textAlign: 'center' }}>
// 																No data. Click plus icon in action column to add new entries.
// 															</TableCell>
// 														</TableRow>
// 													)}
// 												</TableBody>
// 											</Table>
// 										</TableContainer>

// 										<Form.Item name="grossRents_new" noStyle />
// 									</>
// 								)}
// 							</Form.List>
// 						</>
// 					</CardAccordion>

// 					<Row gutter={[16, 0]}>
// 						<Col xs={24} md={6}>
// 							<InputLabel label={constantFormValues?.paymentFrequency?.label} head={true} />
// 						</Col>

// 						<Col xs={24} md={6}>
// 							<Form.Item
// 								getValueFromEvent={handlePaymentFrequencyChange}
// 								name={`${constantFormValues?.paymentFrequency?.key}_new`}
// 								rules={constantFormValues?.paymentFrequency?.rules}
// 							>
// 								<Select
// 									showSearch
// 									filterOption={true}
// 									optionFilterProp="children"
// 									placeholder="Select from the list"
// 									onBlur={handlePaymentFrequencyBlur}
// 									style={{ width: '100%', height: '32px', borderRadius: '4px' }}
// 								>
// 									{constantFormValues?.paymentFrequency?.options?.map(({ key, value, label }) => (
// 										<Select.Option key={key} value={value}>
// 											{label}
// 										</Select.Option>
// 									))}
// 								</Select>
// 							</Form.Item>
// 						</Col>

// 						<Col xs={24} md={6}>
// 							<InputLabel label={constantFormValues?.paymentModality?.label} head={true} />
// 						</Col>

// 						<Col xs={24} md={6}>
// 							<Form.Item
// 								getValueFromEvent={handlePaymentModalityChange}
// 								rules={constantFormValues?.paymentModality?.rules}
// 								name={`${constantFormValues?.paymentModality?.key}_new`}
// 							>
// 								<Select
// 									showSearch
// 									filterOption={true}
// 									optionFilterProp="children"
// 									onBlur={handlePaymentModalityBlur}
// 									placeholder="Select from the list"
// 									style={{ width: '100%', height: '32px', borderRadius: '4px' }}
// 								>
// 									{constantFormValues?.paymentModality?.options?.map(({ key, value, label }) => (
// 										<Select.Option key={key} value={value}>
// 											{label}
// 										</Select.Option>
// 									))}
// 								</Select>
// 							</Form.Item>
// 						</Col>
// 					</Row>
// 				</Col>

// 				<Col xs={24} md={12}>
// 					<CardAccordion
// 						sx={{ mb: 2 }}
// 						darkTitle={false}
// 						title="Advance Payment & Adjustment"
// 						typographySX={{ fontSize: '16px !important' }}
// 						customHeaderSX={{ backgroundColor: '#e1f0fb !important', p: '10px' }}
// 					>
// 						<Form.Item name="advanceAmountListSet" noStyle />

// 						<Form.List name={'advanceAmountList'}>
// 							{(fields) => {
// 								return (
// 									<>
// 										<TableContainer
// 											className="gross-rent-table"
// 											sx={{
// 												'& table, & th, & td': {
// 													border: '1px solid #e0e0e0',
// 													borderCollapse: 'collapse',
// 												},
// 												'& th, & td': {
// 													p: '5px',
// 												},
// 												'& td .ant-form-item': {
// 													marginBottom: '0px',
// 												},

// 												'& th:nth-of-type(1), & td:nth-of-type(1)': { width: '30%' } /* From/From Month */,
// 												'& th:nth-of-type(2), & td:nth-of-type(2)': { width: '30%' } /* To/To Month */,
// 												'& th:nth-of-type(3), & td:nth-of-type(3)': { width: '30%' } /* Amount/Monthly */,

// 												'& table': { width: '100%' } /* Ensure table takes full container width */,
// 											}}
// 										>
// 											<Table aria-label="Gross Rent table">
// 												<TableHead>
// 													<TableRow>
// 														<TableCell sx={{ textAlign: 'center' }}>From Month</TableCell>
// 														<TableCell sx={{ textAlign: 'center' }}>To Month</TableCell>
// 														<TableCell sx={{ textAlign: 'center' }}>Monthly Adjustment</TableCell>
// 													</TableRow>
// 												</TableHead>
// 												<TableBody>
// 													{fields.length > 0 ? (
// 														fields.map((field, index) => (
// 															<TableRow key={field.key}>
// 																<TableCell>
// 																	<Form.Item name={[field.name, 'from']}>
// 																		<Input
// 																			disabled={true}
// 																			style={{
// 																				width: '100%',
// 																				padding: '8px',
// 																				textAlign: 'center',
// 																				backgroundColor: '#f5f5f5',
// 																			}}
// 																		/>
// 																	</Form.Item>
// 																</TableCell>

// 																<TableCell>
// 																	<Form.Item
// 																		name={[field.name, 'to']}
// 																		rules={[
// 																			{ required: false, message: "Please enter a 'To' value" },
// 																			({ getFieldValue }) => ({
// 																				validator(_, value) {
// 																					const leaseType = getFieldValue('leaseType');
// 																					const fromValue = getFieldValue([
// 																						'advanceAmountList',
// 																						field.name,
// 																						'from',
// 																					]);
// 																					if (!value || !fromValue) return Promise.resolve();
// 																					if (parseInt(value) < parseInt(fromValue)) {
// 																						return Promise.reject(
// 																							new Error("'To' must be greater than 'From'")
// 																						);
// 																					}
// 																					if (leaseType === 'Operating Lease' && parseInt(value) > 12) {
// 																						return Promise.reject(new Error('Maximum month is 12'));
// 																					}
// 																					return Promise.resolve();
// 																				},
// 																			}),
// 																		]}
// 																	>
// 																		<Input
// 																			disabled
// 																			type="number"
// 																			placeholder="Enter To value"
// 																			style={{
// 																				padding: '8px',
// 																				width: '100%',

// 																				textAlign: 'center',
// 																			}}
// 																			onChange={() =>
// 																				handleToChangeAdvanceAmount(`${'advanceAmountList'}.${field.name}.to`)
// 																			}
// 																			onBlur={() => validateToValueOnBlurAdvanceAmount(index)}
// 																		/>
// 																	</Form.Item>
// 																</TableCell>
// 																<TableCell>
// 																	<Form.Item
// 																		name={[field.name, 'amount']}
// 																		rules={[{ required: false, message: 'Please enter an amount' }]}
// 																	>
// 																		<NumericFormat
// 																			disabled
// 																			size="small"
// 																			decimalScale={2}
// 																			placeholder="0.00"
// 																			fixedDecimalScale
// 																			thousandSeparator=","
// 																			isAllowed={withValueCap}
// 																			style={{
// 																				padding: '18px',
// 																				textAlign: 'right',
// 																				height: '32px',
// 																				borderRadius: '4px',
// 																				fontSize: '13px',
// 																			}}
// 																			customInput={Input}
// 																			onValueChange={(values) => {
// 																				handleAmountChangeAdvanceAmount(
// 																					`${'advanceAmountList'}.${field.name}.amount`
// 																				);
// 																				// First, get the current form values
// 																				const currentValues = [...form.getFieldValue('advanceAmountList')];

// 																				// Update the current field with the new value (or empty string if deleted)
// 																				if (currentValues[index]) {
// 																					// Update with the numeric value (not formatted)
// 																					currentValues[index].amount = values.value || '';

// 																					// Update the form with the new value
// 																					form.setFieldValue('advanceAmountList', currentValues);

// 																					// Calculate row total for this specific row
// 																					const fromValue = parseInt(currentValues[index].from);
// 																					const toValue = parseInt(currentValues[index].to);

// 																					// If amount is now empty, set row total to 0
// 																					if (!values.value) {
// 																						const newRowTotals = [...rowTotals];
// 																						newRowTotals[index] = 0;
// 																						setRowTotals(newRowTotals);

// 																						// Calculate new grand total from row totals
// 																						const newTotal = newRowTotals.reduce(
// 																							(total, rowTotal) => total + (rowTotal || 0),
// 																							0
// 																						);
// 																						setTotalAmount(newTotal);
// 																						return;
// 																					}

// 																					// Otherwise calculate normally
// 																					const amount = parseFloat(values.value);

// 																					if (
// 																						!isNaN(fromValue) &&
// 																						!isNaN(toValue) &&
// 																						!isNaN(amount) &&
// 																						toValue >= fromValue
// 																					) {
// 																						const newRowTotals = [...rowTotals];
// 																						newRowTotals[index] = (toValue - fromValue + 1) * amount;
// 																						setRowTotals(newRowTotals);

// 																						// Calculate new grand total from row totals
// 																						const newTotal = newRowTotals.reduce(
// 																							(total, rowTotal) => total + (rowTotal || 0),
// 																							0
// 																						);
// 																						setTotalAmount(newTotal);
// 																					}
// 																				}
// 																			}}
// 																			// Add an onBlur handler to ensure calculation happens
// 																			onBlur={() => {
// 																				setTimeout(() => {
// 																					calculateRowTotals();
// 																					calculateTotal();
// 																					handleApplyAdvanceAmount();
// 																				}, 10);
// 																			}}
// 																		/>
// 																	</Form.Item>
// 																</TableCell>
// 															</TableRow>
// 														))
// 													) : (
// 														<TableRow>
// 															<TableCell colSpan={5} style={{ textAlign: 'center' }}>
// 																No data. Click the plus icon to add new entries.
// 															</TableCell>
// 														</TableRow>
// 													)}
// 												</TableBody>
// 												<TableFooter>
// 													<TableRow sx={{ height: 24 }}>
// 														<TableCell colSpan={2} align="right" style={{ fontWeight: 'bold' }}>
// 															<InputLabel
// 																label={'Advance Payment'}
// 																head={true}
// 																sx={{
// 																	justifyContent: 'end',
// 																	marginBottom: '0px',
// 																}}
// 															/>
// 														</TableCell>
// 														<TableCell style={{ fontWeight: 'bold', textAlign: 'right' }}>
// 															<NumericFormat
// 																value={totalAmount || 0}
// 																displayType="input"
// 																decimalScale={2}
// 																fixedDecimalScale
// 																thousandSeparator=","
// 																size="small"
// 																style={{
// 																	padding: '18px',
// 																	textAlign: 'right',
// 																	height: '32px',
// 																	borderRadius: '4px',
// 																	fontSize: '13px',
// 																	fontWeight: 'bold',
// 																	backgroundColor: undefined,
// 																	borderColor: undefined,
// 																}}
// 																customInput={Input}
// 																disabled={true}
// 																readOnly={true}
// 															/>
// 														</TableCell>
// 													</TableRow>
// 												</TableFooter>
// 											</Table>
// 										</TableContainer>

// 										{/* Hidden form field to store processed data for API submission */}
// 										<Form.Item name="advanceAmounts" noStyle />
// 									</>
// 								);
// 							}}
// 						</Form.List>
// 					</CardAccordion>
// 				</Col>

// 				<Col xs={24} md={12}>
// 					<CardAccordion
// 						sx={{ mb: 2 }}
// 						darkTitle={false}
// 						title="Advance Payment & Adjustment"
// 						typographySX={{ fontSize: '16px !important' }}
// 						customHeaderSX={{ backgroundColor: '#e1f0fb !important', p: '10px' }}
// 					>
// 						<Form.Item name="advanceAmountListSet" noStyle />

// 						<Form.List name={'advanceAmountList'}>
// 							{(fields) => {
// 								return (
// 									<>
// 										<TableContainer
// 											className="gross-rent-table"
// 											sx={{
// 												'& table, & th, & td': {
// 													border: '1px solid #e0e0e0',
// 													borderCollapse: 'collapse',
// 												},
// 												'& th, & td': {
// 													p: '5px',
// 												},
// 												'& td .ant-form-item': {
// 													marginBottom: '0px',
// 												},

// 												'& th:nth-of-type(1), & td:nth-of-type(1)': { width: '30%' } /* From/From Month */,
// 												'& th:nth-of-type(2), & td:nth-of-type(2)': { width: '30%' } /* To/To Month */,
// 												'& th:nth-of-type(3), & td:nth-of-type(3)': { width: '30%' } /* Amount/Monthly */,

// 												'& table': { width: '100%' } /* Ensure table takes full container width */,
// 											}}
// 										>
// 											<Table aria-label="Gross Rent table">
// 												<TableHead>
// 													<TableRow>
// 														<TableCell sx={{ textAlign: 'center' }}>From Month</TableCell>
// 														<TableCell sx={{ textAlign: 'center' }}>To Month</TableCell>
// 														<TableCell sx={{ textAlign: 'center' }}>Monthly Adjustment</TableCell>
// 													</TableRow>
// 												</TableHead>
// 												<TableBody>
// 													{fields.length > 0 ? (
// 														fields.map((field, index) => (
// 															<TableRow key={field.key}>
// 																<TableCell>
// 																	<Form.Item name={[field.name, 'from']}>
// 																		<Input
// 																			disabled={true} // Always disabled to prevent manual editing
// 																			style={{
// 																				padding: '8px',
// 																				width: '100%',
// 																				backgroundColor: '#f5f5f5', // Grey background to indicate disabled
// 																				textAlign: 'center',
// 																			}}
// 																		/>
// 																	</Form.Item>
// 																</TableCell>

// 																<TableCell>
// 																	<Form.Item
// 																		name={[field.name, 'to']}
// 																		rules={[
// 																			{ required: false, message: "Please enter a 'To' value" },
// 																			({ getFieldValue }) => ({
// 																				validator(_, value) {
// 																					const leaseType = getFieldValue('leaseType');
// 																					const fromValue = getFieldValue([
// 																						'advanceAmountList',
// 																						field.name,
// 																						'from',
// 																					]);
// 																					if (!value || !fromValue) return Promise.resolve();
// 																					if (parseInt(value) < parseInt(fromValue)) {
// 																						return Promise.reject(
// 																							new Error("'To' must be greater than 'From'")
// 																						);
// 																					}
// 																					if (leaseType === 'Operating Lease' && parseInt(value) > 12) {
// 																						return Promise.reject(new Error('Maximum month is 12'));
// 																					}
// 																					return Promise.resolve();
// 																				},
// 																			}),
// 																		]}
// 																	>
// 																		<Input
// 																			disabled
// 																			type="number"
// 																			placeholder="Enter To value"
// 																			style={{
// 																				padding: '8px',
// 																				width: '100%',

// 																				textAlign: 'center',
// 																			}}
// 																			onChange={() =>
// 																				handleToChangeAdvanceAmount(`${'advanceAmountList'}.${field.name}.to`)
// 																			}
// 																			onBlur={() => validateToValueOnBlurAdvanceAmount(index)}
// 																		/>
// 																	</Form.Item>
// 																</TableCell>
// 																<TableCell>
// 																	<Form.Item
// 																		name={[field.name, 'amount']}
// 																		rules={[{ required: false, message: 'Please enter an amount' }]}
// 																	>
// 																		<NumericFormat
// 																			disabled
// 																			size="small"
// 																			decimalScale={2}
// 																			placeholder="0.00"
// 																			fixedDecimalScale
// 																			thousandSeparator=","
// 																			isAllowed={withValueCap}
// 																			style={{
// 																				padding: '18px',
// 																				textAlign: 'right',
// 																				height: '32px',
// 																				borderRadius: '4px',
// 																				fontSize: '13px',
// 																			}}
// 																			customInput={Input}
// 																			onValueChange={(values) => {
// 																				handleAmountChangeAdvanceAmount(
// 																					`${'advanceAmountList'}.${field.name}.amount`
// 																				);
// 																				// First, get the current form values
// 																				const currentValues = [...form.getFieldValue('advanceAmountList')];

// 																				// Update the current field with the new value (or empty string if deleted)
// 																				if (currentValues[index]) {
// 																					// Update with the numeric value (not formatted)
// 																					currentValues[index].amount = values.value || '';

// 																					// Update the form with the new value
// 																					form.setFieldValue('advanceAmountList', currentValues);

// 																					// Calculate row total for this specific row
// 																					const fromValue = parseInt(currentValues[index].from);
// 																					const toValue = parseInt(currentValues[index].to);

// 																					// If amount is now empty, set row total to 0
// 																					if (!values.value) {
// 																						const newRowTotals = [...rowTotals];
// 																						newRowTotals[index] = 0;
// 																						setRowTotals(newRowTotals);

// 																						// Calculate new grand total from row totals
// 																						const newTotal = newRowTotals.reduce(
// 																							(total, rowTotal) => total + (rowTotal || 0),
// 																							0
// 																						);
// 																						setTotalAmount(newTotal);
// 																						return;
// 																					}

// 																					// Otherwise calculate normally
// 																					const amount = parseFloat(values.value);

// 																					if (
// 																						!isNaN(fromValue) &&
// 																						!isNaN(toValue) &&
// 																						!isNaN(amount) &&
// 																						toValue >= fromValue
// 																					) {
// 																						const newRowTotals = [...rowTotals];
// 																						newRowTotals[index] = (toValue - fromValue + 1) * amount;
// 																						setRowTotals(newRowTotals);

// 																						// Calculate new grand total from row totals
// 																						const newTotal = newRowTotals.reduce(
// 																							(total, rowTotal) => total + (rowTotal || 0),
// 																							0
// 																						);
// 																						setTotalAmount(newTotal);
// 																					}
// 																				}
// 																			}}
// 																			// Add an onBlur handler to ensure calculation happens
// 																			onBlur={() => {
// 																				setTimeout(() => {
// 																					calculateRowTotals();
// 																					calculateTotal();
// 																					handleApplyAdvanceAmount();
// 																				}, 10);
// 																			}}
// 																		/>
// 																	</Form.Item>
// 																</TableCell>
// 															</TableRow>
// 														))
// 													) : (
// 														<TableRow>
// 															<TableCell colSpan={5} style={{ textAlign: 'center' }}>
// 																No data. Click the plus icon to add new entries.
// 															</TableCell>
// 														</TableRow>
// 													)}
// 												</TableBody>
// 												<TableFooter>
// 													<TableRow sx={{ height: 24 }}>
// 														<TableCell colSpan={2} align="right" style={{ fontWeight: 'bold' }}>
// 															<InputLabel
// 																label={'Advance Payment'}
// 																head={true}
// 																sx={{
// 																	justifyContent: 'end',
// 																	marginBottom: '0px',
// 																}}
// 															/>
// 														</TableCell>
// 														<TableCell style={{ fontWeight: 'bold', textAlign: 'right' }}>
// 															<NumericFormat
// 																value={totalAmount || 0}
// 																displayType="input"
// 																decimalScale={2}
// 																fixedDecimalScale
// 																thousandSeparator=","
// 																size="small"
// 																style={{
// 																	padding: '18px',
// 																	textAlign: 'right',
// 																	height: '32px',
// 																	borderRadius: '4px',
// 																	fontSize: '13px',
// 																	fontWeight: 'bold',
// 																	backgroundColor: undefined,
// 																	borderColor: undefined,
// 																}}
// 																customInput={Input}
// 																disabled={true}
// 																readOnly={true}
// 															/>
// 														</TableCell>
// 													</TableRow>
// 												</TableFooter>
// 											</Table>
// 										</TableContainer>

// 										{/* Hidden form field to store processed data for API submission */}
// 										<Form.Item name="advanceAmounts" noStyle />
// 									</>
// 								);
// 							}}
// 						</Form.List>
// 					</CardAccordion>
// 				</Col>

// 				<Col xs={24}>
// 					<CardAccordion
// 						darkTitle={false}
// 						title="Other Payments & Incentive"
// 						contentSX={{ paddingBottom: '0 !important' }}
// 						typographySX={{ fontSize: '16px !important' }}
// 						customHeaderSX={{ backgroundColor: '#e1f0fb !important', p: '10px' }}
// 					>
// 						<Row gutter={[16, 0]}>
// 							<Col xs={24} md={6}>
// 								<InputLabel label={constantFormValues?.upfrontLeasePayment?.label} head={true} />
// 							</Col>
// 							<Col xs={24} md={9}>
// 								<Form.Item name={constantFormValues?.upfrontLeasePayment?.key} rules={constantFormValues?.upfrontLeasePayment?.rules}>
// 									<NumericFormat
// 										disabled
// 										size="large"
// 										decimalScale={2}
// 										placeholder="0.00"
// 										fixedDecimalScale
// 										customInput={Input}
// 										thousandSeparator=","
// 										isAllowed={withValueCap}
// 										style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
// 									/>
// 								</Form.Item>
// 							</Col>
// 							<Col xs={24} md={9}>
// 								<Form.Item
// 									name={`${constantFormValues?.upfrontLeasePayment?.key}_new`}
// 									rules={constantFormValues?.upfrontLeasePayment?.rules}
// 								>
// 									<NumericFormat
// 										disabled
// 										size="large"
// 										decimalScale={2}
// 										placeholder="0.00"
// 										fixedDecimalScale
// 										customInput={Input}
// 										thousandSeparator=","
// 										isAllowed={withValueCap}
// 										style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
// 									/>
// 								</Form.Item>
// 							</Col>

// 							<Col xs={24} md={6}>
// 								<InputLabel label={constantFormValues?.initialDirectCost?.label} head={true} />
// 							</Col>
// 							<Col xs={24} md={9}>
// 								<Form.Item name={constantFormValues?.initialDirectCost?.key} rules={constantFormValues?.initialDirectCost?.rules}>
// 									<NumericFormat
// 										disabled
// 										size="large"
// 										decimalScale={2}
// 										placeholder="0.00"
// 										fixedDecimalScale
// 										customInput={Input}
// 										thousandSeparator=","
// 										isAllowed={withValueCap}
// 										style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
// 									/>
// 								</Form.Item>
// 							</Col>
// 							<Col xs={24} md={9}>
// 								<Form.Item
// 									rules={constantFormValues?.initialDirectCost?.rules}
// 									name={`${constantFormValues?.initialDirectCost?.key}_new`}
// 								>
// 									<NumericFormat
// 										disabled
// 										size="large"
// 										decimalScale={2}
// 										placeholder="0.00"
// 										fixedDecimalScale
// 										customInput={Input}
// 										thousandSeparator=","
// 										isAllowed={withValueCap}
// 										style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
// 									/>
// 								</Form.Item>
// 							</Col>

// 							<Col xs={24} md={6}>
// 								<InputLabel label={constantFormValues?.securityDeposit?.label} head={true} />
// 							</Col>
// 							<Col xs={24} md={9}>
// 								<Form.Item name={constantFormValues?.securityDeposit?.key} rules={constantFormValues?.securityDeposit?.rules}>
// 									<NumericFormat
// 										disabled
// 										size="large"
// 										decimalScale={2}
// 										placeholder="0.00"
// 										fixedDecimalScale
// 										customInput={Input}
// 										thousandSeparator=","
// 										isAllowed={withValueCap}
// 										style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
// 									/>
// 								</Form.Item>
// 							</Col>
// 							<Col xs={24} md={9}>
// 								<Form.Item
// 									rules={constantFormValues?.securityDeposit?.rules}
// 									name={`${constantFormValues?.securityDeposit?.key}_new`}
// 								>
// 									<NumericFormat
// 										disabled
// 										size="large"
// 										decimalScale={2}
// 										placeholder="0.00"
// 										fixedDecimalScale
// 										customInput={Input}
// 										thousandSeparator=","
// 										isAllowed={withValueCap}
// 										style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
// 									/>
// 								</Form.Item>
// 							</Col>

// 							<Col xs={24} md={6}>
// 								<InputLabel label={constantFormValues?.cashIncentiveAmount?.label} head={true} />
// 							</Col>
// 							<Col xs={24} md={9}>
// 								<Form.Item name={constantFormValues?.cashIncentiveAmount?.key} rules={constantFormValues?.securityDeposit?.rules}>
// 									<NumericFormat
// 										disabled
// 										size="large"
// 										decimalScale={2}
// 										fixedDecimalScale
// 										placeholder="0.00"
// 										customInput={Input}
// 										thousandSeparator=","
// 										isAllowed={withValueCap}
// 										style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
// 									/>
// 								</Form.Item>
// 							</Col>
// 							<Col xs={24} md={9}>
// 								<Form.Item
// 									rules={constantFormValues?.securityDeposit?.rules}
// 									name={`${constantFormValues?.cashIncentiveAmount?.key}_new`}
// 								>
// 									<NumericFormat
// 										disabled
// 										size="large"
// 										decimalScale={2}
// 										fixedDecimalScale
// 										placeholder="0.00"
// 										customInput={Input}
// 										thousandSeparator=","
// 										isAllowed={withValueCap}
// 										style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
// 									/>
// 								</Form.Item>
// 							</Col>
// 						</Row>
// 					</CardAccordion>
// 				</Col>

// 				<Form.Item name="grossRents" noStyle />
// 				<Form.Item name="grossRents_new" noStyle />
// 				<Form.Item name="advanceAmounts" noStyle />
// 				<Form.Item name="advanceAmounts_new" noStyle />
// 				<Form.Item name={constantFormValues?.advanceAmount?.key} noStyle />
// 				<Form.Item name={`${constantFormValues?.advanceAmount?.key}_new`} noStyle />
// 				<Form.Item noStyle name={constantFormValues?.inputChangeMonth?.key} />
// 			</Row>

// 			<Row gutter={[16, 16]}>
// 				<Col xs={24}>
// 					<Form.List name="changes">
// 						{(fields) => {
// 							return (
// 								<MainCard content={false} sx={{ mb: 2, mt: '12px', borderColor: '#d9d9d9' }}>
// 									<TableContainer
// 										sx={{
// 											width: '100%',
// 											overflowX: 'auto',
// 											position: 'relative',
// 											display: 'block',
// 											'& td, & th': { whiteSpace: 'nowrap' },
// 											'& td': { borderTop: '0px', borderLeft: '0px', borderBottom: '0px' },
// 											'& td:not(:last-child)': { borderRight: '1px solid #e0e0e0' },
// 											'& tr:not(:last-child)': { borderBottom: '1px solid #e0e0e0' },
// 											'& th': { borderBottom: '1px solid #e0e0e0' },
// 										}}
// 									>
// 										<Table
// 											sx={{
// 												'& .MuiTableBody-root .MuiTableCell-root': { px: 0.5, py: 0.5 },
// 												tableLayout: 'fixed',
// 											}}
// 										>
// 											<TableHead>
// 												<TableRow>
// 													<Form.Item noStyle shouldUpdate>
// 														{({ getFieldValue }) => {
// 															const leaseType = getFieldValue('leaseType');

// 															return (
// 																<>
// 																	{constantFormValues?.changes?.orderedRowHeader.map((key) => {
// 																		if (key === 'inputChangeDiscountRate' && leaseType === 'Operating Lease')
// 																			return null;

// 																		return (
// 																			<TableCell
// 																				key={constantFormValues?.changes?.changeList[key].key}
// 																				sx={{ fontSize: '14px', backgroundColor: '#e1f0ff !important' }}
// 																				align="center"
// 																			>
// 																				{constantFormValues?.changes?.changeList[key].label}
// 																			</TableCell>
// 																		);
// 																	})}
// 																</>
// 															);
// 														}}
// 													</Form.Item>
// 												</TableRow>
// 											</TableHead>

// 											<TableBody>
// 												{fields?.map((field, index) => {
// 													const maxDate = getMaxDateForRow(index);

// 													return (
// 														<TableRow key={index} style={{ height: '60px' }}>
// 															<TableCell style={{ position: 'relative' }}>
// 																<Form.Item
// 																	style={{ margin: 0 }}
// 																	name={[
// 																		field.name,
// 																		constantFormValues?.changes?.changeList['inputChangeEffectiveDate'].key,
// 																	]}
// 																	rules={constantFormValues?.changes?.changeList['inputChangeEffectiveDate'].rules}
// 																>
// 																	<DatePicker
// 																		// disabled={
// 																		// 	index === 0
// 																		// 		? true
// 																		// 		: screen === 'REVISE'
// 																		// 			? true
// 																		// 			: screen === 'VIEW'
// 																		// 				? true
// 																		// 				: screen === 'EDIT'
// 																		// 					? index < initialChangeLength
// 																		// 					: false
// 																		// }
// 																		// onChange={(date, dateString) => {
// 																		// 	if (date === null || date === undefined || dateString === '') {
// 																		// 		handleInputMonthChange(null, index);
// 																		// 	} else {
// 																		// 		handleInputMonthChange(date, index);
// 																		// 	}
// 																		// }}
// 																		// format="YYYY-MM-DD"
// 																		// style={{ width: '100%', height: '32px' }}
// 																		// className="antd-datePicker"
// 																		// disabledDate={(current) => {
// 																		// 	// Rule 0: Always handle the case where the current date is not provided.
// 																		// 	if (!current) {
// 																		// 		return false;
// 																		// 	}

// 																		// 	// --- SETUP ---
// 																		// 	// Get values from the form.
// 																		// 	const leaseStartDateValue = form.getFieldValue('leaseStartDate');

// 																		// 	// Robustness: If there's no start date yet, don't disable anything.
// 																		// 	if (!leaseStartDateValue) {
// 																		// 		return false;
// 																		// 	}

// 																		// 	const leaseStartDate = dayjs(leaseStartDateValue);

// 																		// 	// Robustness: If the date is invalid, don't crash.
// 																		// 	if (!leaseStartDate.isValid()) {
// 																		// 		return false;
// 																		// 	}

// 																		// 	// --- RULE 1 & 2: DETERMINE THE MINIMUM ALLOWED DATE ("FLOOR") ---
// 																		// 	let minimumAllowedDate = leaseStartDate; // Default to the lease start date.

// 																		// 	// This logic assumes `maxRecordedDate` is passed as a prop,
// 																		// 	// likely when your component's index >= 2.
// 																		// 	if (maxRecordedDate?.minimumDateLimit) {
// 																		// 		// Override the minimum date based on the last recorded action.
// 																		// 		minimumAllowedDate = dayjs(maxRecordedDate.minimumDateLimit)
// 																		// 			.add(1, 'month')
// 																		// 			.startOf('month');
// 																		// 	}
// 																		// 	const isBeforeMinimum = current.isBefore(minimumAllowedDate, 'day');

// 																		// 	// --- RULE 3: DETERMINE THE MAXIMUM ALLOWED DATE ("CEILING") ---
// 																		// 	let isAfterMaximum = false;
// 																		// 	if (maxDate) {
// 																		// 		isAfterMaximum = current.isAfter(dayjs(maxDate), 'day');
// 																		// 	}

// 																		// 	// --- RULE 4: CHECK FOR THE CORRECT DAY OF THE MONTH ---
// 																		// 	const requiredDayOfMonth = leaseStartDate.date();
// 																		// 	const isWrongDayOfMonth = current.date() !== requiredDayOfMonth;

// 																		// 	// --- FINAL DECISION ---
// 																		// 	// Disable the date if it violates ANY of the rules.
// 																		// 	return isAfterMaximum || isWrongDayOfMonth;
// 																		// }}

// 																		disabled={index === 0 ? true : initialChangeLength - 1 < index ? false : true}
// 																		onChange={(date, dateString) => {
// 																			if (date === null || date === undefined || dateString === '') {
// 																				handleInputMonthChange(null, index);
// 																			} else {
// 																				handleInputMonthChange(date, index);
// 																			}
// 																		}}
// 																		format="YYYY-MM-DD"
// 																		style={{ width: '100%', height: '32px' }}
// 																		className="antd-datePicker"
// 																		disabledDate={(current) => {
// 																			if (!current) return false;

// 																			// --- SETUP ---
// 																			const leaseStartDateValue = form.getFieldValue('leaseStartDate');
// 																			if (!leaseStartDateValue) return false;

// 																			const leaseStartDate = dayjs(leaseStartDateValue);
// 																			if (!leaseStartDate.isValid()) return false;

// 																			let minimumAllowedDate = leaseStartDate;

// 																			// If a previous effective date exists (last change row)
// 																			const changes = form.getFieldValue('changes') || [];
// 																			let lastEffectiveDate = null;

// 																			if (index > 0 && changes[index - 1]) {
// 																				lastEffectiveDate =
// 																					changes[index - 1]?.[
// 																						constantFormValues?.changes?.changeList
// 																							?.inputChangeEffectiveDate?.key
// 																					];
// 																			}

// 																			// ✅ Rule: From last effective date + 1 month to lease end date
// 																			if (lastEffectiveDate) {
// 																				minimumAllowedDate = dayjs(lastEffectiveDate)
// 																					.add(1, 'month')
// 																					.startOf('month');
// 																			} else if (maxRecordedDate?.minimumDateLimit) {
// 																				minimumAllowedDate = dayjs(maxRecordedDate.minimumDateLimit)
// 																					.add(1, 'month')
// 																					.startOf('month');
// 																			}

// 																			const leaseEndDate = form.getFieldValue('leaseEndDate');
// 																			let isAfterMaximum = false;
// 																			if (leaseEndDate) {
// 																				isAfterMaximum = current.isAfter(dayjs(leaseEndDate), 'day');
// 																			} else if (maxDate) {
// 																				isAfterMaximum = current.isAfter(dayjs(maxDate), 'day');
// 																			}

// 																			// Rule 4: Required day of month must match lease start day
// 																			const requiredDayOfMonth = leaseStartDate.date();
// 																			const isWrongDayOfMonth = current.date() !== requiredDayOfMonth;

// 																			// Final disable logic
// 																			const isBeforeMinimum = current.isBefore(minimumAllowedDate, 'day');

// 																			return isBeforeMinimum || isAfterMaximum || isWrongDayOfMonth;
// 																		}}
// 																	/>
// 																</Form.Item>
// 															</TableCell>
// 															<TableCell>
// 																<Form.Item
// 																	style={{ margin: 0 }}
// 																	name={[
// 																		field.name,
// 																		constantFormValues?.changes?.changeList['inputChangeDescription'].key,
// 																	]}
// 																>
// 																	<Select
// 																		disabled
// 																		showSearch
// 																		style={{ textAlign: 'center' }}
// 																		filterOption={true}
// 																		options={
// 																			constantFormValues?.changes?.changeList['inputChangeDescription'].options
// 																		}
// 																	/>
// 																</Form.Item>
// 															</TableCell>
// 															<TableCell>
// 																<Form.Item
// 																	style={{ margin: 0 }}
// 																	name={[
// 																		field.name,
// 																		constantFormValues?.changes?.changeList['inputChangeLeaseTerm'].key,
// 																	]}
// 																>
// 																	<Input
// 																		disabled
// 																		type="number"
// 																		style={{
// 																			marginBottom: 0,
// 																			textAlign: 'center',
// 																			color:
// 																				index !== 0
// 																					? form.getFieldValue([
// 																							'changes',
// 																							index,
// 																							'inputChangeLeaseTerm',
// 																						]) ===
// 																						form.getFieldValue([
// 																							'changes',
// 																							index - 1,
// 																							'inputChangeLeaseTerm',
// 																						])
// 																						? 'black'
// 																						: 'blue'
// 																					: 'black',
// 																		}}
// 																	/>
// 																</Form.Item>
// 															</TableCell>
// 															<Form.Item
// 																name={[
// 																	field.name,
// 																	// constantFormValues?.changes?.changeList['inputChangeDiscountRate'].key,
// 																	'inputChangeDiscountRate',
// 																]}
// 																noStyle
// 															/>

// 															<Form.Item noStyle shouldUpdate={(prev, current) => prev.leaseType !== current.leaseType}>
// 																{({ getFieldValue }) =>
// 																	getFieldValue('leaseType') === 'Finance Lease' ? (
// 																		<TableCell align="center">
// 																			<Form.Item
// 																				style={{ margin: 0 }}
// 																				name={[
// 																					field.name,
// 																					constantFormValues?.changes?.changeList['inputChangeDiscountRate']
// 																						.key,
// 																				]}
// 																			>
// 																				<NumericFormat
// 																					size="large"
// 																					decimalScale={2}
// 																					placeholder="0.00"
// 																					fixedDecimalScale
// 																					thousandSeparator=","
// 																					isAllowed={withValueCap}
// 																					style={{
// 																						textAlign: 'center',
// 																						height: '32px',
// 																						borderRadius: '4px',
// 																						fontSize: '13px',
// 																						color:
// 																							index !== 0
// 																								? form.getFieldValue([
// 																										'changes',
// 																										index,
// 																										'inputChangeDiscountRate',
// 																									]) ===
// 																									form.getFieldValue([
// 																										'changes',
// 																										index - 1,
// 																										'inputChangeDiscountRate',
// 																									])
// 																									? 'black'
// 																									: 'blue'
// 																								: 'black',
// 																					}}
// 																					customInput={Input}
// 																					disabled
// 																				/>
// 																			</Form.Item>
// 																		</TableCell>
// 																	) : null
// 																}
// 															</Form.Item>

// 															<TableCell>
// 																<Form.Item
// 																	style={{ margin: 0 }}
// 																	name={[
// 																		field.name,
// 																		constantFormValues?.changes?.changeList['paymentFrequency'].key,
// 																	]}
// 																>
// 																	<Select
// 																		disabled
// 																		showSearch
// 																		filterOption={true}
// 																		style={{ textAlign: 'center' }}
// 																		options={constantFormValues?.changes?.changeList['paymentFrequency'].options}
// 																	/>
// 																</Form.Item>
// 															</TableCell>

// 															<TableCell>
// 																<Form.Item
// 																	style={{ margin: 0 }}
// 																	name={[field.name, constantFormValues?.changes?.changeList['paymentTime'].key]}
// 																>
// 																	<Select
// 																		disabled
// 																		showSearch
// 																		filterOption={true}
// 																		style={{ textAlign: 'center' }}
// 																		options={constantFormValues?.changes?.changeList['paymentTime'].options}
// 																	/>
// 																</Form.Item>
// 															</TableCell>
// 														</TableRow>
// 													);
// 												})}
// 											</TableBody>
// 										</Table>
// 									</TableContainer>
// 								</MainCard>
// 							);
// 						}}
// 					</Form.List>
// 				</Col>
// 			</Row>

// 			<Box
// 				sx={{
// 					gap: '4px',
// 					display: 'flex',
// 					flexDirection: 'column',
// 					justifyContent: 'space-between',
// 				}}
// 			>
// 				<Spin spinning={isLeaseControlDataLoading}>
// 					<Box
// 						sx={{
// 							pt: 0,
// 							gap: '4px',
// 							display: 'flex',
// 							flexDirection: 'row',
// 							justifyContent: 'space-between',
// 						}}
// 					>
// 						<Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
// 							<Button
// 								type="primary"
// 								onClick={handleSubmit}
// 								style={{
// 									marginTop: 0,
// 									backgroundColor: theme.palette.lynTheme.main,
// 								}}
// 							>
// 								Update
// 							</Button>
// 						</Box>
// 					</Box>
// 				</Spin>

// 				{showClosedModal && (
// 					<ComptrollerConfirmModal
// 						modalData={modalData}
// 						showModal={showClosedModal}
// 						setShowModal={setShowClosedModal}
// 						handleSubmit={modalSubmitHandler}
// 					/>
// 				)}

// 				{formState?.showFormSubmitModal && (
// 					<ComptrollerConfirmModal
// 						modalData={formState?.modalData}
// 						handleSubmit={handleModalSubmit}
// 						setShowModal={setShowFormSubmitModal}
// 						showModal={formState?.showFormSubmitModal}
// 					/>
// 				)}
// 			</Box>
// 		</>
// 	);
// };

// export default LeaseRecordManagementV9ModifyBasicInfo;
